<template>
  <div
    class="organization-departments-table"
    :class="{ 'organization-departments-table--idea-mdp-450': ideaMDP450 }"
  >
    <DepartmentsHeaderDashboard
      v-if="departmentsHeaderDashboardEnabled && viewSelection === 'tree'"
      class="departments-header-dashboard-wrapper"
      :active-count="activeDepartmentsCount"
      :draft-count="draftDepartmentsCount"
      :orphan-positions-count="accountActiveCounts.orphanPositions"
      :not-managed-count="accountActiveCounts.departmentsNotManaged"
      :active-indicator="activeIndicatorKey"
      :action-label="activeIndicatorKey ? 'Clear Filter' : 'Apply Filter'"
      @select-indicator="handleIndicatorSelect"
    />
    <div
      v-if="
        shouldShowDepartmentsSearchBar &&
        viewSelection !== 'tree-with-positions' &&
        viewSelection !== 'orphan-positions'
      "
      class="table-header-actions d-flex align-center mb-6 px-4 pt-4"
    >
      <SearchBar
        :label="appliedFilters && appliedFilters.length > 0 ? 'More filters' : 'Filters'"
        :length="appliedFilters ? appliedFilters.length : 0"
        :chipText="appliedFilters || []"
        ghostText="Search Departments by name or code"
        :filterBtn="true"
        :add="true"
        btnText="Add Department"
        actionButtonIcon="plusicon.svg"
        class="flex-grow-1"
        @onClick="handleFilterClick"
        @onSearch="handleSearch"
        @removeFilter="handleRemoveFilter"
        :valueSearch="searchQuery"
        :counterSection="false"
      />
      <div class="right-actions d-flex align-center ml-4">
        <DepartmentsViewsSelector
          :active="viewSelection"
          :orphan-positions-count="orphanPositionsTotal"
          @selection-change="handleViewSelectionChange"
        />
      </div>
    </div>

    <div
      v-if="shouldShowDepartmentsSearchBar && viewSelection === 'tree-with-positions'"
      class="departments-pane-header d-flex align-center px-4 pt-4 mb-4"
    >
      <div class="departments-pane-banner departments-pane-banner--orphan" role="note">
        This view shows positions belonging to departments. Drag and drop them on a department to
        move them.
      </div>
      <DepartmentsViewsSelector
        class="departments-pane-header__view-selector ml-4"
        :active="viewSelection"
        :orphan-positions-count="orphanPositionsTotal"
        @selection-change="handleViewSelectionChange"
      />
    </div>

    <div
      v-if="shouldShowDepartmentsSearchBar && viewSelection === 'tree-with-positions'"
      class="departments-pane-view"
    >
      <div ref="paneViewLayoutRef" class="pane-view-layout">
        <div class="pane-view-left" :style="paneLeftPanelStyle">
          <div
            ref="paneLeftRef"
            :class="[
              'pane-view-left-scroll',
              {
                'pane-view-left-scroll--has-content': departmentTreeData.length > 0,
                'pane-view-left-scroll--empty': departmentTreeData.length === 0,
              },
            ]"
            tabindex="-1"
            @scroll="handleTreeScroll"
            @click.capture="focusLeftScrollContainer"
          >
            <DepartmentsSearchAndNavigate
              ref="departmentsSearchNavigateRef"
              :data="departmentTreeData"
              :selected-item="selectedDepartment"
              :loading-more="treeDepartmentsLoadingMore"
              :has-more="treeDepartmentsHasMore"
              :on-position-dropped-on-department="handlePositionDroppedOnDepartmentTree"
              :position-drag-source="positionDragSource"
              @item-selected="handleDepartmentTreeSelect"
              @position-department-drag-over="handlePositionDragOverState"
            />
          </div>
        </div>

        <div
          class="pane-view-resize-handle"
          role="separator"
          aria-label="Resize panels"
          @mousedown="handlePaneResizeStart"
        />

        <div ref="paneViewRightRef" class="pane-view-right">
          <div v-if="departmentTreeData.length === 0" class="pane-view-empty">
            <div class="empty-state">
              <p>No departments found</p>
            </div>
          </div>
          <DepartmentCard
            v-else-if="selectedDepartment"
            ref="positionDragDepartmentCardRef"
            :key="`department-card-${selectedDepartment.id}`"
            :name="selectedDepartmentCardProps.name"
            :code="selectedDepartmentCardProps.code"
            :status="selectedDepartmentCardProps.status"
            :readiness="selectedDepartmentCardProps.readiness"
            :chatter-unread-count="selectedDepartmentCardProps.chatterUnreadCount"
            :positions-count="selectedDepartmentPositionsCount"
            :context-data="selectedDepartmentDetail"
            :department-id="selectedDepartment?.id ?? null"
            :context-files="departmentContextFiles"
            :uploading-context-files="uploadingDepartmentContextFiles"
            :is-saving-context="isSavingDepartmentContext"
            :positions="departmentPositions"
            :status-options="departmentPositionStatusOptions"
            :filter-groups="departmentPositionFilterGroups"
            :filter-chips="departmentPositionFilterChips"
            :page="departmentPositionsPage"
            :page-count="departmentPositionsPageCount"
            :per-page="effectiveDepartmentPositionsPerPage"
            :menu-items="selectedDepartmentMenuItems"
            :allow-drag-position-to-tree="viewSelection === 'tree-with-positions'"
            :enable-positions-reorder="viewSelection === 'tree-with-positions'"
            :source-department-id="selectedDepartment?.id ?? null"
            :active-tab="departmentActiveTab"
            :on-position-drag-start-callback="handlePositionDragStartFromTable"
            :on-position-drag-end-callback="handlePositionDragEndFromTable"
            @open-department-chatter="openChatter(selectedDepartmentRecord)"
            @open-position-chatter="openPositionChatter"
            @open-department-menu="handleDepartmentCardMenu"
            @back-to-list="handleDepartmentCardBackToList"
            @scope-change="handleDepartmentPositionScopeChange"
            @update:search="handleDepartmentPositionSearch"
            @remove-filter="handleDepartmentPositionRemoveFilter"
            @filters-applied="handleDepartmentPositionFiltersApplied"
            @clear-filters="handleDepartmentPositionClearFilters"
            @update:page="handleDepartmentPositionPageChange"
            @remove-position="handleRemovePositionFromDepartment"
            @reorder="handleDepartmentPositionReorder"
            :get-position-menu-items="getPositionMenuItemsForRow"
            @open-position-menu="handlePositionCardMenu"
            @update:activeTab="departmentActiveTab = $event"
            @context-save="handleDepartmentContextSave"
            @context-upload="handleDepartmentContextUpload"
            @context-remove="handleDepartmentContextRemove"
          />
          <div v-else class="pane-view-empty">
            <div class="empty-state">
              <p>Select a department from the tree to view details</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="viewSelection === 'orphan-positions'"
      class="departments-pane-header d-flex align-center px-4 pt-4 mb-4"
    >
      <div class="departments-pane-banner departments-pane-banner--orphan" role="note">
        <strong>These positions are not assigned to any department.</strong>
        Drag and drop them on a department.
      </div>
      <DepartmentsViewsSelector
        class="departments-pane-header__view-selector ml-4"
        :active="viewSelection"
        :orphan-positions-count="orphanPositionsTotal"
        @selection-change="handleViewSelectionChange"
      />
    </div>

    <div v-if="viewSelection === 'orphan-positions'" class="departments-pane-view">
      <div ref="paneViewLayoutRef" class="pane-view-layout">
        <div class="pane-view-left" :style="paneLeftPanelStyle">
          <div
            ref="paneLeftRef"
            :class="[
              'pane-view-left-scroll',
              {
                'pane-view-left-scroll--has-content': departmentTreeData.length > 0,
                'pane-view-left-scroll--empty': departmentTreeData.length === 0,
              },
            ]"
            tabindex="-1"
            @scroll="handleTreeScroll"
            @click.capture="focusLeftScrollContainer"
          >
            <!-- Orphan view: tree is a drop target only — no click/selection drives the right pane (ER-10007). -->
            <DepartmentsSearchAndNavigate
              ref="departmentsSearchNavigateRef"
              :data="departmentTreeData"
              :selected-item="null"
              :loading-more="treeDepartmentsLoadingMore"
              :has-more="treeDepartmentsHasMore"
              :on-position-dropped-on-department="handlePositionDroppedOnDepartmentTree"
              :position-drag-source="positionDragSource"
              @position-department-drag-over="handlePositionDragOverState"
            />
          </div>
        </div>

        <div
          class="pane-view-resize-handle"
          role="separator"
          aria-label="Resize panels"
          @mousedown="handlePaneResizeStart"
        />

        <div ref="paneViewRightRef" class="pane-view-right">
          <DepartmentCard
            ref="positionDragDepartmentCardRef"
            :positions="orphanPositions"
            :positions-count="orphanPositionsTotal"
            :page="orphanPositionsPage"
            :page-count="orphanPositionsPageCount"
            :per-page="effectiveDepartmentPositionsPerPage"
            :filter-chips="[]"
            :menu-items="[]"
            :hide-header="true"
            :hide-filters="true"
            :show-position-remove="false"
            :allow-drag-position-to-tree="true"
            :enable-positions-reorder="false"
            :source-department-id="null"
            :on-position-drag-start-callback="handlePositionDragStartFromTable"
            :on-position-drag-end-callback="handlePositionDragEndFromTable"
            @update:search="handleOrphanPositionSearch"
            @update:page="handleOrphanPositionPageChange"
            @clear-filters="handleOrphanPositionClearFilters"
            :get-position-menu-items="getPositionMenuItemsForRow"
            @open-position-chatter="openPositionChatter"
            @open-position-menu="handlePositionCardMenu"
          />
        </div>
      </div>
    </div>

    <div
      v-if="shouldShowDepartmentsSearchBar && viewSelection === 'tree'"
      class="table-container"
      :class="{ 'table-container--root-drop-zone': tableRootDropActive }"
      @dragover.prevent="handleTableContainerDragOver"
      @drop.prevent="handleTableContainerDrop"
    >
      <div v-if="isLoadingDepartments" class="loading-skeleton pa-4">
        <v-skeleton-loader type="table-heading, table-row-divider@3" class="elevation-0" />
      </div>
      <table v-else-if="visibleDepartments && visibleDepartments.length > 0" class="simple-table">
        <thead>
          <tr class="header-row">
            <th class="text-left departments-header">Departments</th>
            <th class="positions-count-header text-right">Direct Positions</th>
            <th class="positions-subtree-count-header text-right">Positions Incl Sub-Dpts</th>
            <th class="status-header">
              <div class="status-header-inner d-flex align-center justify-end">
                <span class="status-header-label">Status</span>
                <span class="status-header-actions-spacer" aria-hidden="true"></span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="dept in visibleDepartments"
            :key="dept ? dept.id : Math.random()"
            :draggable="draggableId === dept.id"
            class="tree-row--navigable"
            @click="handleTreeViewRowClick(dept)"
            @dragstart="handleDragStart(dept, $event)"
            @dragover.prevent="handleDragOver(dept, $event)"
            @dragleave="handleDragLeave(dept, $event)"
            @drop.stop="handleDrop(dept, $event)"
            @dragend="handleDragEnd"
            :class="{
              dragging: draggedDept?.id === dept.id,
              'drop-before': dropTargetId === dept.id && dropPosition === 'before',
              'drop-after': dropTargetId === dept.id && dropPosition === 'after',
              'drop-inside': dropTargetId === dept.id && dropPosition === 'inside',
              'drop-promote-root':
                dropTargetId === dept.id &&
                dropPromoteToRoot &&
                (dropPosition === 'before' || dropPosition === 'after'),
              'drop-not-allowed': invalidDropTargetId === dept.id,
            }"
          >
            <td
              v-if="dept"
              class="dept-col"
              :style="{ paddingLeft: `${(dept.level || 0) * 32 + 16}px` }"
            >
              <DepartmentTreeCard
                :state="dept.expansionState || 'No Expand'"
                :code="dept.code || ''"
                :name="dept.name || ''"
                @toggle="handleToggle(dept, $event)"
                @mousedown="draggableId = dept.id"
                @mouseup="draggableId = null"
              />
            </td>
            <td v-if="dept" class="positions-count-col text-right">
              {{ dept.positionsCount != null ? dept.positionsCount : "—" }}
            </td>
            <td v-if="dept" class="positions-subtree-count-col text-right">
              {{ dept.positionsCountSubtree != null ? dept.positionsCountSubtree : "—" }}
            </td>
            <td v-if="dept" class="status-actions-col">
              <div class="d-flex align-center justify-end">
                <OrgObjectStatusBadge :status="dept.status" />
                <div class="icon-action-container ml-6 d-flex align-center">
                  <div class="icon-action chatter-clickable" @click.stop="openChatter(dept)">
                    <ChatterIcon />
                  </div>
                  <div class="icon-action ml-2 department-menu-action" @click.stop>
                    <DepartmentLineMenu
                      :key="`menu-${dept.id}`"
                      :department="dept"
                      :status="getDepartmentStatus(dept)"
                      @action="handleDepartmentAction"
                    />
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <NoRecordFound
        v-else-if="hasActiveSearchOrFilters"
        showSpecificMessage="No department found"
        description="Your filter did not match any department.<br/>Please try again."
        :clearLabel="clearBtnLabel"
        :showClearAction="true"
        :showActiveCounter="false"
        :hideCounter="true"
        :noBorder="true"
        :shouldShowAddAction="false"
        @onClear="handleClearSearch"
      />
    </div>

    <NoRecordFound
      v-if="!shouldShowDepartmentsSearchBar"
      showSpecificMessage="No departments yet"
      description="Create your first department to start organizing positions."
      btnTitle="Department"
      :showActiveCounter="false"
      :hideCounter="true"
      :noBorder="true"
      @onAction="handleAddDepartment"
    />

    <SearchModal
      :dialog="showFilterModal"
      filterType="Departments"
      :statusOptions="statusOptions"
      :filterGroups="departmentsHeaderDashboardEnabled ? managerFilterGroups : []"
      :defaultSelected="appliedFilters"
      :clearSelection="clearSelection"
      @back="showFilterModal = false"
      @checkedItemsChanged="handleFilterChanged"
    />

    <AddDepartmentDialog
      :show="showAddDepartmentDialog"
      :errorMessage="departmentErrorMessage"
      @close="handleCloseAddDepartmentDialog"
      @onAdd="handleAddDepartmentSubmit"
      @clearError="clearDepartmentError"
    />

    <ActivateDepartmentDialog
      :show="showActivateDepartmentDialog"
      :name="activateDepartmentTarget?.name || ''"
      @onClose="handleCloseActivateDepartmentDialog"
      @onActivate="handleActivateDepartmentSubmit"
    />

    <RetireDepartmentDialog
      :show="showRetireDepartmentDialog"
      :name="retireDepartmentTarget?.name || ''"
      @onClose="handleCloseRetireDepartmentDialog"
      @onRetire="handleRetireDepartmentSubmit"
    />

    <DeleteDepartmentDialog
      :show="showDeleteDepartmentDialog"
      :name="deleteDepartmentTarget?.name || ''"
      @onClose="handleCloseDeleteDepartmentDialog"
      @onDelete="handleDeleteDepartmentSubmit"
    />

    <RenameDepartmentDialog
      v-if="showRenameDepartmentDialog"
      :key="renameDepartmentTarget?.id || 'rename-department'"
      :value="showRenameDepartmentDialog"
      :departmentId="renameDepartmentTarget?.id"
      :name="renameDepartmentTarget?.name || ''"
      type="rename"
      :errorMessage="departmentErrorMessage"
      @onClose="handleCloseRenameDepartmentDialog"
      @onSave="handleRenameDepartmentSubmit"
      @clearError="clearDepartmentError"
    />

    <ReactivateDepartmentDialog
      :show="showReactivateDepartmentDialog"
      :name="reactivateDepartmentTarget?.name || ''"
      @onClose="handleCloseReactivateDepartmentDialog"
      @onReactivate="handleReactivateDepartmentSubmit"
    />

    <ArchiveDepartmentDialog
      :show="showArchiveDepartmentDialog"
      :name="archiveDepartmentTarget?.name || ''"
      @onClose="handleCloseArchiveDepartmentDialog"
      @onArchive="handleArchiveDepartmentSubmit"
    />

    <UnarchiveDepartmentDialog
      :show="showUnarchiveDepartmentDialog"
      :name="unarchiveDepartmentTarget?.name || ''"
      :errorMessage="unarchiveDepartmentError"
      @onClose="handleCloseUnarchiveDepartmentDialog"
      @onUnarchive="handleUnarchiveDepartmentSubmit"
    />

    <UnarchiveNameConflictDialog
      :show="unarchiveDepartmentConflictOpen"
      objectType="department"
      :currentName="unarchiveDepartmentTarget?.name || ''"
      :objectId="unarchiveDepartmentTarget?.id || ''"
      :accountId="currentAccountId"
      :busy="unarchiveDepartmentBusy"
      :errorMessage="unarchiveDepartmentError"
      :nameError="unarchiveDepartmentNameError"
      @confirm="unarchiveDepartment.submitRename"
      @close="handleCloseUnarchiveDepartmentDialog"
      @clear-error="unarchiveDepartment.clearErrors"
    />

    <ChatterDrawer
      ref="drawerContainer"
      :drawer="drawer"
      :iconName="iconName"
      :type="typeName"
      :typeValue="typeValue"
      :listData="listData"
      :object-type="typeName"
      :object-id="listData?.id ?? null"
      :chatterList="chatterData"
      :totalCheckpointChatter="totalChatterData"
      @closeDrawer="closeDrawer"
      @requestMoreCheckpointsChatter="handleRequestMoreChatter"
    />

    <Teleport to="body">
      <template v-if="paneTreeExternalDragActive">
        <div class="pane-checkpoint-drag-cursor-overlay" aria-hidden="true" />
        <div ref="paneDragCursorRef" class="pane-checkpoint-drag-cursor">
          <DragAndDropInPositionsTreeNavigatorCursor
            action="Attach"
            :state="positionDragCursorState"
          />
        </div>
      </template>
    </Teleport>

    <DropToAttachRoleToPositionDialog
      :value="showDropToAttachPositionDialog"
      :title="
        pendingPositionAttach?.sourceDepartmentId != null ? 'Move position' : 'Assign position'
      "
      :intro-line="pendingPositionAttach?.sourceDepartmentId != null ? 'Move' : 'Assign'"
      :role-name="pendingPositionAttach?.positionLabel || ''"
      :middle-line="
        pendingPositionAttach?.sourceDepartmentId != null
          ? `from ${pendingPositionAttach?.sourceDepartmentName || ''} to`
          : 'to'
      "
      :position-name="pendingPositionAttach?.targetDepartmentLabel || ''"
      :button-label="pendingPositionAttach?.sourceDepartmentId != null ? 'Move' : 'Assign'"
      cancel-label="Cancel"
      icon-name="org_icon_position"
      :inline-message="true"
      content-class="assign-position-dialog-centered"
      @attach="handleConfirmDropToAttachPosition"
      @input="onDropToAttachPositionDialogInput"
    />

    <DetachPositionDialog
      v-model="showReleasePositionDialog"
      parent-kind="department"
      action-label="Remove"
      :position_name="positionToRelease?.label || ''"
      :parent_name="selectedDepartmentCardProps?.name || ''"
      @onAction="handleConfirmReleasePosition"
    />

    <RenamePositionDialog
      v-if="showRenamePositionDialog"
      v-model="showRenamePositionDialog"
      :name="activePosition?.name || ''"
      :error-message="renamePositionError"
      @clearError="renamePositionError = ''"
      @onSave="handleRenamePositionSubmit"
    />

    <ClonePositionDialog
      v-if="showClonePositionDialog"
      v-model="showClonePositionDialog"
      :positionName="activePosition?.name || ''"
      @onAction="handleClonePositionSubmit"
    />

    <AttachedRole
      v-model="showAttachRoleToPositionDialog"
      OrgObjectType="Role to Position"
      :items="attachableRolesForPosition"
      :parent-name="activePosition?.name || ''"
      :openDefault="attachRoleOpenDefault"
      type="attach_role_to_position_new_picker"
      :hasMoreData="attachableRolesHasMore"
      :isLoadingMore="attachableRolesLoadingMore"
      :isAssigning="attachRoleAssigning"
      @onSave="handleAttachRoleToPositionSubmit"
      @item-selected="handleAttachRoleSelected"
      @add-item-selected="handleAttachNewRoleToPosition"
      @load-more="loadMoreAttachableRolesForPosition"
      @search="searchAttachableRolesForPosition"
    />

    <RetirePositionDialog
      v-model="showRetirePositionDialog"
      :name="activePosition?.name || ''"
      @onAction="handleRetirePositionSubmit"
    />

    <ReactivatePositionDialog
      v-model="showReactivatePositionDialog"
      :name="activePosition?.name || ''"
      @onAction="handleReactivatePositionSubmit"
    />

    <ArchivePositionDialog
      v-if="showArchivePositionDialog && positionArchiveAction === 'archive'"
      v-model="showArchivePositionDialog"
      :name="activePosition?.name || ''"
      :error-message="archivePositionError"
      @onAction="handleArchivePositionSubmit"
    />

    <UnarchivePositionDialog
      v-if="showArchivePositionDialog && positionArchiveAction === 'unarchive'"
      v-model="showArchivePositionDialog"
      :name="activePosition?.name || ''"
      :error-message="archivePositionError"
      @onAction="handleArchivePositionSubmit"
    />

    <ActivatePositionDialog
      v-model="showActivatePositionDialog"
      :name="activePosition?.name || ''"
      @onAction="handleActivatePositionSubmit"
    />

    <DeletePositionDialog
      v-model="showDeletePositionDialog"
      :name="activePosition?.name || ''"
      @onAction="handleDeletePositionSubmit"
    />

    <div
      v-if="invalidDropReason"
      class="department-drag-invalid-label"
      :style="invalidDropLabelStyle"
      role="status"
      aria-live="polite"
    >
      {{ invalidDropReason }}
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  nextTick,
  watch,
  Teleport,
} from "vue";
import {
  addDepartment,
  editDepartment,
  activateDepartment,
  retireDepartment,
  deleteDepartment as deleteDepartmentApi,
  reActivateDepartment,
  archiveDepartment as archiveDepartmentApi,
  getListOfDepartments,
  getDepartment,
  getChatterList,
  getPositionsForDepartment,
  getOrphanPositions,
  assignPositionToDepartment,
  movePositionToDepartment,
  releasePositionFromDepartment,
  reorderPositionsInDepartment,
  reorderDepartment,
  updatePosition,
  createPosition,
  attachRoleToPosition,
  fetchAttachableRolesForPosition,
  createRole,
  retirePosition,
  reActivatePosition,
  archivePosition,
  unArchivePosition,
  activatePosition,
  deletePosition,
  getListOfRolesAttachedToPosition,
  getContextFiles,
  uploadContextFiles,
  deleteContextFile,
  mapContextFileToUi,
} from "@/api";
import { useDropdownItems } from "@/composables/dropdown/useDropdownItems";
import {
  mapApiDepartmentsToTableRows,
  mapApiDepartmentsToTreeData,
  mapDepartmentDetailToCardProps,
  mapApiPositionToDepartmentCardRow,
  DEFAULT_DEPARTMENT_READINESS,
  mapDepartmentToTableRow,
  mapFlatDepartmentRowsToTreeData,
} from "@/utils/departmentTableMapper";
import {
  DEPARTMENT_POSITION_STATUS_VALUES,
  convertDepartmentPositionFiltersToApi,
  resolveDepartmentPositionArchivedFilter,
} from "@/utils/departmentPositionFilters";
import {
  DEPARTMENT_INDICATOR_FILTERS,
  DEPARTMENT_MANAGER_FILTER_GROUPS,
  resolveActiveIndicatorKey,
  resolveHasManagerFilter,
} from "@/utils/departmentIndicatorFilters";
import { clearStuckPositionDragArtifacts } from "@/utils/positionDragCleanup";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import DepartmentsViewsSelector from "@components/DepartmentsViewsSelector/DepartmentsViewsSelector.vue";
import DepartmentsSearchAndNavigate from "@components/DepartmentsSearchAndNavigate/DepartmentsSearchAndNavigate.vue";
import DepartmentsHeaderDashboard from "@components/DepartmentsHeaderDashboard/DepartmentsHeaderDashboard.vue";
import DepartmentCard, {
  ORG_OBJECT_STATUS,
  DEPARTMENT_CARD_TABS,
} from "@components/DepartmentCard/DepartmentCard.vue";
import {
  HEADER_DASHBOARD_FLAGS,
} from "@components/constants/launchDarklyFlagKeys";
import DepartmentTreeCard from "@components/DepartmentTreeCard/DepartmentTreeCard.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import DepartmentLineMenu from "@components/DepartmentLineMenu/DepartmentLineMenu.vue";

import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import AddDepartmentDialog from "@components/AddDepartmentDialog/AddDepartmentDialog.vue";
import ActivateDepartmentDialog from "@components/ActivateDepartmentDialog/ActivateDepartmentDialog.vue";
import RetireDepartmentDialog from "@components/RetireDepartmentDialog/RetireDepartmentDialog.vue";
import DeleteDepartmentDialog from "@components/DeleteDepartmentDialog/DeleteDepartmentDialog.vue";
import { NAME_IN_USE, nameConflictMessage } from "@components/constants/orgObjectMessages";
import { getApiErrorDetail } from "@components/utils/apiErrorDetail";
import RenameDepartmentDialog from "@components/RenameDepartmentDialog/RenameDepartmentDialog.vue";
import ReactivateDepartmentDialog from "@components/ReactivateDepartmentDialog/ReactivateDepartmentDialog.vue";
import ArchiveDepartmentDialog from "@components/ArchiveDepartmentDialog/ArchiveDepartmentDialog.vue";
import UnarchiveDepartmentDialog from "@components/UnarchiveDepartmentDialog/UnarchiveDepartmentDialog.vue";
import UnarchiveNameConflictDialog from "@components/UnarchiveNameConflictDialog/UnarchiveNameConflictDialog.vue";
import { useUnarchiveOrgObject } from "@/composables/useUnarchiveOrgObject";
import DragAndDropInPositionsTreeNavigatorCursor from "@components/DragAndDropInPositionsTreeNavigatorCursor/DragAndDropInPositionsTreeNavigatorCursor.vue";
import DropToAttachRoleToPositionDialog from "@components/RolesCatalog/DropToAttachRoleToPositionDialog/DropToAttachRoleToPositionDialog.vue";
import DetachPositionDialog from "@components/DetachPositionDialog/DetachPositionDialog.vue";
import RenamePositionDialog from "@components/PositionDialogs/RenamePositionDialog/RenamePositionDialog.vue";
import ClonePositionDialog from "@components/ClonePositionDialog/ClonePositionDialog.vue";
import AttachedRole from "@components/RolesCatalog/AttachedRole.vue";
import RetirePositionDialog from "@components/PositionDialogs/RetirePositionDialog/RetirePositionDialog.vue";
import ReactivatePositionDialog from "@components/PositionDialogs/ReactivatePositionDialog/ReactivatePositionDialog.vue";
import ArchivePositionDialog from "@components/PositionDialogs/ArchivePositionDialog/ArchivePositionDialog.vue";
import UnarchivePositionDialog from "@components/PositionDialogs/UnarchivePositionDialog/UnarchivePositionDialog.vue";
import ActivatePositionDialog from "@components/PositionDialogs/ActivatePositionDialog/ActivatePositionDialog.vue";
import DeletePositionDialog from "@components/PositionDialogs/DeletePositionDialog/DeletePositionDialog.vue";
import { getAppData, getFilters, updateFilters } from "@components/helper/localStorageHelper";
import { bindExiqtiveAccessFiltersChanged } from "@components/utils/bindExiqtiveAccessFiltersChanged";
import { positionActionErrorMessage } from "@components/utils/positionErrorMessages";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
import {
  createLatestRequestGuard,
  stableKey,
} from "@components/utils/createLatestRequestGuard";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";
import { refreshAccountCounts } from "@/utils/accountCountUtils";

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const dropdownItems = useDropdownItems();

/** LaunchDarkly idea-MDP-450 — keep department search placeholders fully visible. */
const ideaMDP450 = computed(() => Boolean(proxy?.$store?.getters?.["featureFlags/ideaMDP450"]));

/**
 * MDP-514 / ER-10686 — departments header dashboard (Active / Draft / Orphan
 * Positions / Not Managed) above the Departments tree. Gated by its own
 * idea-MDP-514 flag (shared with Department Manager Assignment), not the
 * idea-MDP-558 gate used by the Employees and Positions list dashboards.
 */
const departmentsHeaderDashboardEnabled = computed(
  () => Boolean(proxy?.$store?.getters?.[HEADER_DASHBOARD_FLAGS.departments.storeGetter])
);

/**
 * ER-12439 / idea-MDP-622 — the Departments header dashboard reuses the shared
 * sidebar counts store (fed by login and every lifecycle mutation). It never
 * fetches active-counts itself: the shared refresh owns the request.
 */
const sidebarCountStore = useSidebarCountStore();

/**
 * Account-wide counts (non-archived positions without a department and active
 * departments without an open DepartmentHeadAssignment) read from the store, so
 * they update reactively when a background refresh lands.
 */
const accountActiveCounts = computed(() => ({
  orphanPositions: sidebarCountStore.getOrphanPositionsCount,
  departmentsNotManaged: sidebarCountStore.getDepartmentsNotManagedCount,
}));

const refreshAccountActiveCounts = async () => {
  const accountID = localStorage.getItem("currentAccountID");

  // The store is the single source of truth. Load it once if it has never been
  // populated; the shared refresh is single-flight and debounced, so this cannot
  // duplicate a mutation-triggered request. Flag off: refreshAccountCounts
  // awaits the legacy active-counts call, so the tiles are still filled before
  // the page finishes loading.
  //
  // Nothing has to be refetched after a department mutation: every helper that
  // changes these counts (add/delete/activate/retire/archive/unarchive and the
  // position assign/move/release/create/delete helpers) schedules the same
  // shared refresh, so the store stays current for every screen.
  if (!sidebarCountStore.hasAccountCounts) {
    await refreshAccountCounts(accountID);
  }
};

const emit = defineEmits(["toggle-expansion", "department-action", "reorder"]);
const storeFilterKey = "organizationDepartmentsData";
const defaultDepartmentFilters = getDefaultFiltersForKey("organizationDepartmentsData");

const searchQuery = ref("");
const appliedFilters = ref([...defaultDepartmentFilters]);
const internalDepartments = ref([]);
const isLoadingDepartments = ref(false);

const countDepartmentsByStatus = (status) => {
  const rows = Array.isArray(internalDepartments.value) ? internalDepartments.value : [];
  const normalized = String(status || "").toLowerCase();
  return rows.filter((dept) => String(dept?.status || "").toLowerCase() === normalized).length;
};

/**
 * Per-status counts of the currently loaded department list (filter-aware —
 * narrow with the applied search/status filters, like the sibling dashboards).
 */
const activeDepartmentsCount = computed(() => countDepartmentsByStatus("active"));
const draftDepartmentsCount = computed(() => countDepartmentsByStatus("draft"));

const hasStoredDepartmentFilters = () => {
  const appData = getAppData();
  if (!appData) return false;
  if (appData.filters && Object.prototype.hasOwnProperty.call(appData.filters, storeFilterKey)) {
    return true;
  }
  const byAccess = appData.filtersByAccess;
  if (byAccess && typeof byAccess === "object") {
    return Object.values(byAccess).some(
      (bucket) => bucket && Object.prototype.hasOwnProperty.call(bucket, storeFilterKey)
    );
  }
  return false;
};

const applySavedFilters = () => {
  const savedFilters = getFilters(storeFilterKey);
  const normalized = Array.isArray(savedFilters) ? savedFilters : [];

  // Legacy path: unset key returns [] from getFiltersLegacy — keep UI defaults without
  // marking filters as "non-default" (ER-10194 fresh Access / empty list UX).
  if (normalized.length === 0 && !hasStoredDepartmentFilters()) {
    appliedFilters.value = [...defaultDepartmentFilters];
    return;
  }

  appliedFilters.value =
    normalized.length > 0 ? [...normalized] : [...defaultDepartmentFilters];
};

const saveDepartmentFilters = (filters = appliedFilters.value) => {
  updateFilters(storeFilterKey, Array.isArray(filters) ? filters : []);
};

const convertDepartmentFiltersToApi = (filters = appliedFilters.value) => {
  const status = (filters || [])
    .map((filter) => (typeof filter === "string" ? filter : filter?.value || "").toLowerCase())
    .filter((value) => ["draft", "active", "retired", "archived", "inactive"].includes(value));

  const apiFilters = status.length > 0 ? { status } : {};
  const hasManager = resolveHasManagerFilter(filters);
  if (hasManager !== undefined) apiFilters.has_manager = hasManager;
  return apiFilters;
};

const managerFilterGroups = DEPARTMENT_MANAGER_FILTER_GROUPS;

/** ER-12340 — tile whose filter combination equals the applied filters; survives chip/modal edits. */
const activeIndicatorKey = computed(() =>
  searchQuery.value?.trim() ? null : resolveActiveIndicatorKey(appliedFilters.value)
);

const hideLoaderAfterRender = async () => {
  await nextTick();
  await nextTick();
  proxy?.$loader?.hide?.();
};

const TREE_PAGE_SIZE = 10;

const isPaneTreeView = () =>
  viewSelection.value === "tree-with-positions" || viewSelection.value === "orphan-positions";

const getDepartmentPositionsPageSize = () =>
  isPaneTreeView() ? TREE_PAGE_SIZE : departmentPositionsPerPage.value;

const effectiveDepartmentPositionsPerPage = computed(() => getDepartmentPositionsPageSize());

const normalizeDepartmentListResponse = (data) => {
  if (Array.isArray(data)) {
    return { results: data, count: data.length };
  }
  const results = data?.results || [];
  return {
    results,
    count: data?.count ?? results.length,
  };
};

const normalizeApiErrorText = (value) => {
  if (Array.isArray(value)) {
    const first = value.find((item) => item != null && String(item).trim());
    return first != null ? String(first) : "";
  }
  if (value == null || value === "") return "";
  return String(value);
};

const findDepartmentInTree = (nodes, id) => {
  if (!Array.isArray(nodes) || id == null) return false;
  return nodes.some((node) => {
    if (String(node?.id) === String(id)) return true;
    return findDepartmentInTree(node?.children, id);
  });
};

const findDepartmentNodeInTree = (nodes, id) => {
  if (!Array.isArray(nodes) || id == null) return null;
  for (const node of nodes) {
    if (String(node?.id) === String(id)) return node;
    const found = findDepartmentNodeInTree(node?.children, id);
    if (found) return found;
  }
  return null;
};

const positionDragSessionActive = ref(false);
const positionDragSource = ref(null);
const isPositionToDepartmentDragging = ref(false);
const positionDragCursorState = ref("Dragging");
const paneDragCursorRef = ref(null);
const positionDragDepartmentCardRef = ref(null);
const showDropToAttachPositionDialog = ref(false);
const pendingPositionAttach = ref(null);
const showReleasePositionDialog = ref(false);
const positionToRelease = ref(null);
const activePosition = ref(null);
const showRenamePositionDialog = ref(false);
const renamePositionError = ref("");
const archivePositionError = ref("");
const showClonePositionDialog = ref(false);
const showAttachRoleToPositionDialog = ref(false);
const showRetirePositionDialog = ref(false);
const showReactivatePositionDialog = ref(false);
const showArchivePositionDialog = ref(false);
const showActivatePositionDialog = ref(false);
const showDeletePositionDialog = ref(false);
const positionArchiveAction = ref("archive");
const attachableRolesForPosition = ref([]);
const attachableRolesHasMore = ref(false);
const attachableRolesLoadingMore = ref(false);
const attachRoleAssigning = ref(false);
const attachRoleOpenDefault = ref(false);
const selectedAttachRoleId = ref(null);
const attachableRolesPage = ref(1);
const attachableRolesSearch = ref("");
const ATTACHABLE_ROLES_PAGE_SIZE = 20;
let positionDragEndCleanup = null;
let positionDragSourceDepartmentId = null;

const paneTreeExternalDragActive = computed(
  () =>
    (viewSelection.value === "tree-with-positions" || viewSelection.value === "orphan-positions") &&
    positionDragSessionActive.value &&
    isPositionToDepartmentDragging.value
);

const paneDragCursorLeftOffset = 100;
const paneDragCursorLeftInsetPx = 8;
const paneDragCursorTopInsetPx = 6;

// Drag-and-drop order is stored as `sequence`; ordering by name would hide it.
const DEPARTMENT_LIST_ORDERING = "sequence";

// ER-11705: the Organization > Departments table loads through this component-local
// function. Collapse concurrent identical loads into one request and drop a
// superseded response so the newest filter always wins.
const loadDepartmentsGuard = createLatestRequestGuard();

const loadDepartments = async ({
  search = searchQuery.value,
  filters = convertDepartmentFiltersToApi(appliedFilters.value),
} = {}) => {
  isLoadingDepartments.value = true;
  proxy?.$loader?.show?.();

  let run;
  try {
    const accountID = localStorage.getItem("currentAccountID");
    const key = stableKey({
      accountId: accountID,
      search,
      filters,
      ordering: DEPARTMENT_LIST_ORDERING,
    });

    run = loadDepartmentsGuard.start(key, async (entry) => {
      const { data } = await getListOfDepartments(accountID, search, {
        ...filters,
        ordering: DEPARTMENT_LIST_ORDERING,
      });

      // A newer request (different key) may have started while this one was in
      // flight. Discard this stale response so the newest filter always wins.
      if (!entry.isCurrent()) {
        return data;
      }

      internalDepartments.value = await Promise.resolve(mapApiDepartmentsToTableRows(data));
      return data;
    });

    await run.promise;
  } catch (error) {
    console.error("Error loading departments:", error);
    if (!run || run.isCurrent()) {
      internalDepartments.value = [];
    }
  } finally {
    if (!run || run.isCurrent()) {
      isLoadingDepartments.value = false;
    }
  }

  await hideLoaderAfterRender();

  // Header dashboard account counts (orphan positions / not managed) only make
  // sense against the full (unfiltered) department list; skip narrowed searches.
  if (
    departmentsHeaderDashboardEnabled.value &&
    !search.trim() &&
    !hasNonDefaultFiltersApplied.value
  ) {
    await refreshAccountActiveCounts();
  }
};

const reloadDepartments = async () => {
  await loadDepartments({
    search: searchQuery.value,
    filters: convertDepartmentFiltersToApi(appliedFilters.value),
  });
  await refreshPaneTreeIfActive();
};

const reloadAfterDepartmentAdded = async (addedName) => {
  const name = typeof addedName === "string" ? addedName.trim() : "";
  if (name) {
    searchQuery.value = name;
    await loadDepartments({
      search: name,
      filters: convertDepartmentFiltersToApi(appliedFilters.value),
    });
    return;
  }
  await reloadDepartments();
};

const hasRecords = computed(() => (internalDepartments.value?.length ?? 0) > 0);

const hasSearchApplied = computed(() => Boolean(searchQuery.value?.trim()));

const hasNonDefaultFiltersApplied = computed(() => {
  const filters = appliedFilters.value || [];
  return (
    filters.length !== defaultDepartmentFilters.length ||
    filters.some((filter) => !defaultDepartmentFilters.includes(filter))
  );
});

const hasActiveSearchOrFilters = computed(() => {
  return hasSearchApplied.value || hasNonDefaultFiltersApplied.value;
});

const clearBtnLabel = computed(() => {
  if (hasSearchApplied.value && hasNonDefaultFiltersApplied.value) {
    return "Clear All";
  }
  if (hasSearchApplied.value) {
    return "Clear Search";
  }
  if (hasNonDefaultFiltersApplied.value) {
    return "Clear Filter";
  }
  return "Clear";
});

const shouldShowDepartmentsSearchBar = computed(() => {
  if (isLoadingDepartments.value) {
    return true;
  }
  const hasNoItems = !hasRecords.value;
  const hasNoSearch = !searchQuery.value?.trim();
  const hasNoFilters = !hasActiveSearchOrFilters.value;
  const hideSearchBar = hasNoItems && hasNoSearch && hasNoFilters;
  return !hideSearchBar;
});

const viewSelection = ref("tree");
const selectedDepartment = ref(null);
/** DepartmentCard tab state (Positions vs Context). */
const departmentActiveTab = ref(DEPARTMENT_CARD_TABS.POSITIONS);
const isSavingDepartmentContext = ref(false);
/** Uploaded context files for the selected department (ER-10294 context-files API). */
const departmentContextFiles = ref([]);
/** Files mid-upload, rendered as progress-bar cards until the list refresh replaces them. */
const uploadingDepartmentContextFiles = ref([]);
const selectedDepartmentDetail = ref(null);
const departmentPositions = ref([]);
const departmentPositionsTotal = ref(0);
const departmentPositionsPage = ref(1);
const departmentPositionsPerPage = ref(10);
const departmentPositionSearch = ref("");
const DEPARTMENT_POSITIONS_SCOPE_SUBTREE = "subtree";
const DEPARTMENT_POSITIONS_SCOPE_DIRECT = "direct";
/** Backend `department_scope` for the selected department's positions. Stays null (no param,
 * backend default = direct) until the card reports its scope toggle is shown (idea-MDP-509). */
const departmentPositionsScope = ref(null);
let departmentPositionsRequestId = 0;
const departmentPositionFilterChips = ref([]);
const departmentPositionStatusOptions = DEPARTMENT_POSITION_STATUS_VALUES;
const departmentPositionFilterGroups = [
  {
    label: "Archived",
    items: [
      { label: "Do not Show", value: "no_archived" },
      { label: "Show", value: "show_archived" },
    ],
  },
];
const defaultDepartmentPositionFilters = ["draft", "active"];
const orphanPositions = ref([]);
const orphanPositionsTotal = ref(0);
const orphanPositionsPage = ref(1);
const orphanPositionSearch = ref("");
const paneLeftWidthPercent = ref(33);
const paneLeftRef = ref(null);
const paneViewRightRef = ref(null);
const paneViewLayoutRef = ref(null);
const paneViewRightHeight = ref(null);
const paneLeftPanelStyle = computed(() => {
  const style = {
    width: `${paneLeftWidthPercent.value}%`,
  };
  if (paneViewRightHeight.value != null) {
    const heightPx = `${paneViewRightHeight.value}px`;
    style.height = heightPx;
    style.minHeight = heightPx;
    style.maxHeight = heightPx;
  }
  return style;
});
const departmentsSearchNavigateRef = ref(null);
const treeDepartmentRows = ref([]);
const treeDepartmentsTotal = ref(0);
const treeDepartmentsLoadingMore = ref(false);
let paneResizeActive = false;
let paneViewRightResizeObserver = null;
let wheelListenerEl = null;
const showAddDepartmentDialog = ref(false);
const showActivateDepartmentDialog = ref(false);
const activateDepartmentTarget = ref(null);
const showRetireDepartmentDialog = ref(false);
const retireDepartmentTarget = ref(null);
const showDeleteDepartmentDialog = ref(false);
const deleteDepartmentTarget = ref(null);
const showRenameDepartmentDialog = ref(false);
const renameDepartmentTarget = ref(null);
const showReactivateDepartmentDialog = ref(false);
const reactivateDepartmentTarget = ref(null);
const showArchiveDepartmentDialog = ref(false);
const archiveDepartmentTarget = ref(null);
const showUnarchiveDepartmentDialog = ref(false);
const currentAccountId = localStorage.getItem("currentAccountID");
const unarchiveDepartment = useUnarchiveOrgObject("department", {
  getAccountId: () => localStorage.getItem("currentAccountID"),
  openConfirm: () => {
    showUnarchiveDepartmentDialog.value = true;
  },
  closeConfirm: () => {
    showUnarchiveDepartmentDialog.value = false;
  },
  onUnarchived: () => reloadDepartments(),
  setLoading: (value) => proxy?.$loader?.setAppLoading?.(value),
});
const {
  target: unarchiveDepartmentTarget,
  conflictOpen: unarchiveDepartmentConflictOpen,
  busy: unarchiveDepartmentBusy,
  errorMessage: unarchiveDepartmentError,
  nameError: unarchiveDepartmentNameError,
} = unarchiveDepartment;
const departmentErrorMessage = ref("");
const showFilterModal = ref(false);
const statusOptions = ref(["draft", "active", "retired", "archived"]);
const clearSelection = ref("");

const drawer = ref(false);
const listData = ref({});
const typeName = ref("");
const iconName = ref("");
const typeValue = ref("");
const drawerContainer = ref(null);
const chatterData = ref([]);
const totalChatterData = ref(0);
const chatterRecordType = ref("department");

const draggedDept = ref(null);
const dropTargetId = ref(null);
const dropPosition = ref(null); // 'before' | 'after' | 'inside'
const dropPromoteToRoot = ref(false);
const tableRootDropActive = ref(false);
const draggableId = ref(null);
const invalidDropTargetId = ref(null);
const invalidDropReason = ref(null);
const invalidDropCoords = ref({ x: 0, y: 0 });

const invalidDropLabelStyle = computed(() => ({
  left: `${invalidDropCoords.value.x + 12}px`,
  top: `${invalidDropCoords.value.y + 20}px`,
}));

let debouncedSearch = null;

// Auto-scroll during drag
let autoScrollFrame = null;
let lastDragClientY = 0;
const SCROLL_ZONE = 80;
const SCROLL_SPEED = 12;

const autoScrollLoop = () => {
  const el = document.scrollingElement || document.documentElement;
  const viewHeight = window.innerHeight;

  if (lastDragClientY < SCROLL_ZONE) {
    el.scrollTop -= SCROLL_SPEED;
  } else if (lastDragClientY > viewHeight - SCROLL_ZONE) {
    el.scrollTop += SCROLL_SPEED;
  }

  autoScrollFrame = requestAnimationFrame(autoScrollLoop);
};

const startAutoScroll = (clientY) => {
  lastDragClientY = clientY;
  if (!autoScrollFrame) {
    autoScrollFrame = requestAnimationFrame(autoScrollLoop);
  }
};

const stopAutoScroll = () => {
  if (autoScrollFrame) {
    cancelAnimationFrame(autoScrollFrame);
    autoScrollFrame = null;
  }
  lastDragClientY = 0;
};

const debounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
};

const filteredDepartments = computed(() => {
  if (!Array.isArray(internalDepartments.value)) return [];
  return internalDepartments.value;
});

const departmentTreeData = computed(() => {
  if (isPaneTreeView()) {
    return mapApiDepartmentsToTreeData(treeDepartmentRows.value);
  }
  return mapFlatDepartmentRowsToTreeData(filteredDepartments.value);
});

const treeDepartmentsHasMore = computed(
  () => treeDepartmentRows.value.length < treeDepartmentsTotal.value
);

const departmentPositionsPageCount = computed(() =>
  Math.max(1, Math.ceil(departmentPositionsTotal.value / getDepartmentPositionsPageSize()))
);

const orphanPositionsPageCount = computed(() =>
  Math.max(1, Math.ceil(orphanPositionsTotal.value / getDepartmentPositionsPageSize()))
);

const selectedDepartmentRecord = computed(() => {
  if (!selectedDepartment.value?.id) return null;
  const selectedId = String(selectedDepartment.value.id);
  const rawTreeDept = treeDepartmentRows.value.find((dept) => String(dept?.id) === selectedId);
  if (rawTreeDept) {
    return mapDepartmentToTableRow(rawTreeDept, rawTreeDept.level ?? 0, false);
  }
  return internalDepartments.value.find((dept) => String(dept?.id) === selectedId) || null;
});

const mapDepartmentStatusToCard = (status) => {
  const normalized = String(status || "draft").toLowerCase();
  return normalized === "active" ? ORG_OBJECT_STATUS.ACTIVE : ORG_OBJECT_STATUS.DRAFT;
};

const selectedDepartmentCardProps = computed(() => {
  if (selectedDepartmentDetail.value) {
    const detail = mapDepartmentDetailToCardProps(selectedDepartmentDetail.value);
    return {
      ...detail,
      status: mapDepartmentStatusToCard(detail.status),
    };
  }

  const dept = selectedDepartmentRecord.value;
  if (!dept) {
    return {
      name: selectedDepartment.value?.name || "",
      code: String(selectedDepartment.value?.code || "").replace(/^#/, ""),
      status: ORG_OBJECT_STATUS.ACTIVE,
      readiness: { ...DEFAULT_DEPARTMENT_READINESS },
      chatterUnreadCount: 0,
    };
  }

  return {
    name: dept.name || "",
    code: String(dept.code || "").replace(/^#/, ""),
    status: mapDepartmentStatusToCard(dept.status),
    readiness: { ...DEFAULT_DEPARTMENT_READINESS },
    chatterUnreadCount: 0,
  };
});

const selectedDepartmentPositionsCount = computed(() => departmentPositionsTotal.value);

const selectedDepartmentMenuItems = computed(() => {
  const dept = selectedDepartmentRecord.value;
  if (!dept) return [];
  const status = getDepartmentStatus(dept);
  return [
    status === "Draft" && { name: "Activate", value: "activate" },
    status === "Draft" && { name: "Delete", value: "delete", destructive: true },
    status === "Active" && { name: "Retire", value: "retire" },
    status === "Retired" && { name: "Reactivate", value: "reactivate" },
    { name: "Rename", value: "rename" },
    status === "Retired" && { name: "Archive", value: "archive" },
    status === "Archived" && { name: "Unarchive", value: "unarchive" },
  ].filter(Boolean);
});

const visibleDepartments = computed(() => {
  const result = [];
  let minHiddenLevel = Infinity;
  const depts = filteredDepartments.value || [];

  depts.forEach((dept) => {
    if (!dept) return;
    const level = dept.level || 0;
    if (level <= minHiddenLevel) {
      result.push(dept);
      if (dept.expansionState === "Collapsed") {
        minHiddenLevel = level;
      } else {
        minHiddenLevel = Infinity;
      }
    }
  });

  return result;
});

const handleSearch = (query) => {
  searchQuery.value = query || "";
  if (debouncedSearch) {
    debouncedSearch(searchQuery.value);
  } else {
    loadDepartments({
      search: searchQuery.value,
      filters: convertDepartmentFiltersToApi(appliedFilters.value),
    });
  }
};

const refreshSelectedDepartmentDetail = async () => {
  if (selectedDepartment.value?.id) {
    await loadDepartmentDetail(selectedDepartment.value.id);
  }
};

const refreshPaneTreeIfActive = async () => {
  if (!isPaneTreeView()) return;
  await rebuildTreeDepartments();
  if (viewSelection.value !== "tree-with-positions") {
    if (viewSelection.value === "orphan-positions" && departmentTreeData.value.length > 0) {
      await expandFirstDepartmentInTree();
    }
    await ensurePaneViewHeightSync();
    return;
  }
  if (departmentTreeData.value.length === 0) {
    selectedDepartment.value = null;
    return;
  }
  if (
    !selectedDepartment.value ||
    !findDepartmentInTree(departmentTreeData.value, selectedDepartment.value.id)
  ) {
    selectFirstDepartment();
  }
  await refreshSelectedDepartmentDetail();
  await ensurePaneViewHeightSync();
};

const handleRemoveFilter = async (removedFilter) => {
  clearSelection.value = removedFilter;
  appliedFilters.value = appliedFilters.value.filter((filter) => filter !== removedFilter);
  saveDepartmentFilters();
  await loadDepartments({
    search: searchQuery.value,
    filters: convertDepartmentFiltersToApi(appliedFilters.value),
  });
  await refreshPaneTreeIfActive();
};

const handleFilterChanged = async (checkedItems) => {
  appliedFilters.value = (checkedItems || []).map((item) => item.value).filter(Boolean);
  saveDepartmentFilters();
  showFilterModal.value = false;
  await loadDepartments({
    search: searchQuery.value,
    filters: convertDepartmentFiltersToApi(appliedFilters.value),
  });
  await refreshPaneTreeIfActive();
};

const handleClearSearch = async () => {
  searchQuery.value = "";
  appliedFilters.value = [...defaultDepartmentFilters];
  saveDepartmentFilters();
  clearSelection.value = "clear-all";
  await loadDepartments({
    search: "",
    filters: convertDepartmentFiltersToApi(appliedFilters.value),
  });
  await refreshPaneTreeIfActive();
};

/**
 * ER-12340 — header tile click. Replaces the whole filter state (search text
 * included) with the tile's combination; clicking the active tile restores the
 * default filters.
 */
const handleIndicatorSelect = async (key) => {
  const target = DEPARTMENT_INDICATOR_FILTERS[key];
  if (!target) return;

  debouncedSearch?.cancel();
  searchQuery.value = "";
  appliedFilters.value =
    activeIndicatorKey.value === key ? [...defaultDepartmentFilters] : [...target];
  saveDepartmentFilters();
  await loadDepartments({
    search: "",
    filters: convertDepartmentFiltersToApi(appliedFilters.value),
  });
  await refreshPaneTreeIfActive();
};

const handleFilterClick = (type) => {
  if (type === "filter") {
    showFilterModal.value = true;
  } else if (type === "add") {
    handleAddDepartment();
  }
};

const clearDepartmentError = () => {
  departmentErrorMessage.value = "";
};

const handleCloseAddDepartmentDialog = () => {
  if (proxy?.$loader?.isAppLoading) return;
  showAddDepartmentDialog.value = false;
  clearDepartmentError();
};

const handleAddDepartment = () => {
  clearDepartmentError();
  showAddDepartmentDialog.value = true;
};

const handleAddDepartmentSubmit = async (name) => {
  const departmentName = typeof name === "string" ? name.trim() : "";
  if (!departmentName) return;

  try {
    proxy?.$loader?.setAppLoading?.(true);
    clearDepartmentError();
    const accountID = localStorage.getItem("currentAccountID");
    const { data } = await addDepartment(accountID, { name: departmentName });

    if (data?.id) {
      const addedName = (typeof data?.name === "string" && data.name.trim()) || departmentName;
      showAddDepartmentDialog.value = false;
      await reloadAfterDepartmentAdded(addedName);
    }
  } catch (error) {
    console.error("Error creating department:", error);
    const apiDetail = getApiErrorDetail(error);
    const apiError = error.response?.data?.[0] || error.response?.data?.name?.[0];
    if (apiDetail) {
      departmentErrorMessage.value = apiDetail;
    } else if (apiError === "Same name already exists" || apiError?.includes?.("already exists")) {
      departmentErrorMessage.value = "Name already exists. Only unique names are allowed.";
    } else {
      departmentErrorMessage.value = apiError || "An error occurred while creating the department.";
    }
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
};

const handleToggle = (dept, newState) => {
  const target = internalDepartments.value.find((d) => d.id === dept.id);
  if (target) {
    target.expansionState = newState;
  }
  emit("toggle-expansion", { dept, newState });
};

const loadDepartmentDetail = async (departmentId = selectedDepartment.value?.id) => {
  const accountID = localStorage.getItem("currentAccountID");
  if (!accountID || !departmentId) {
    selectedDepartmentDetail.value = null;
    return;
  }

  try {
    const { data } = await getDepartment(accountID, departmentId);
    selectedDepartmentDetail.value = data || null;
    void loadDepartmentContextFiles(departmentId);
  } catch (error) {
    console.error("Error loading department detail:", error);
    selectedDepartmentDetail.value = null;
  }
};

/** ER-10294: refresh the Context Files list for the selected department. */
const loadDepartmentContextFiles = async (departmentId) => {
  try {
    const { data } = await getContextFiles("department", departmentId);
    const list = Array.isArray(data) ? data : data?.results || [];
    departmentContextFiles.value = list.map(mapContextFileToUi);
  } catch (error) {
    console.error("Error loading context files:", error);
  }
};

/** ER-9904: persist department.context via the department PATCH endpoint, then refresh detail. */
const handleDepartmentContextSave = async (value) => {
  const accountID = localStorage.getItem("currentAccountID");
  const departmentId = selectedDepartment.value?.id;
  if (!accountID || !departmentId) return;
  try {
    isSavingDepartmentContext.value = true;
    await editDepartment(accountID, departmentId, { context: value });
    await loadDepartmentDetail(departmentId);
  } catch (error) {
    console.error("Error updating department context:", error);
  } finally {
    isSavingDepartmentContext.value = false;
  }
};

const handleDepartmentContextUpload = async (fileList) => {
  const departmentId = selectedDepartment.value?.id;
  if (!fileList?.length || !departmentId) return;

  uploadingDepartmentContextFiles.value = Array.from(fileList).map((file) => ({
    name: file.name,
    progress: 0,
  }));

  try {
    isSavingDepartmentContext.value = true;
    await uploadContextFiles("department", departmentId, fileList, (event) => {
      if (!event.total) return;
      const progress = Math.round((event.loaded / event.total) * 100);
      uploadingDepartmentContextFiles.value = uploadingDepartmentContextFiles.value.map((file) => ({
        ...file,
        progress,
      }));
    });
    await loadDepartmentContextFiles(departmentId);
  } catch (error) {
    console.error("Error uploading context file:", error);
  } finally {
    isSavingDepartmentContext.value = false;
    uploadingDepartmentContextFiles.value = [];
  }
};

const handleDepartmentContextRemove = async (fileId) => {
  const departmentId = selectedDepartment.value?.id;
  if (!fileId || !departmentId) return;

  try {
    isSavingDepartmentContext.value = true;
    await deleteContextFile(fileId);
    await loadDepartmentContextFiles(departmentId);
  } catch (error) {
    console.error("Error removing context file:", error);
  } finally {
    isSavingDepartmentContext.value = false;
  }
};

const loadDepartmentPositions = async (departmentId = selectedDepartment.value?.id) => {
  const accountID = localStorage.getItem("currentAccountID");
  if (!accountID || !departmentId) {
    departmentPositions.value = [];
    departmentPositionsTotal.value = 0;
    return;
  }

  const requestId = ++departmentPositionsRequestId;
  try {
    const pageSize = getDepartmentPositionsPageSize();
    const offset = (departmentPositionsPage.value - 1) * pageSize;
    const { data } = await getPositionsForDepartment(accountID, departmentId, {
      search: departmentPositionSearch.value,
      status: convertDepartmentPositionFiltersToApi(departmentPositionFilterChips.value),
      archived: resolveDepartmentPositionArchivedFilter(departmentPositionFilterChips.value),
      limit: pageSize,
      offset,
      departmentScope: departmentPositionsScope.value,
    });
    if (requestId !== departmentPositionsRequestId) return;
    const results = data?.results || [];
    const totalCount = data?.count ?? results.length;

    if (results.length === 0 && offset > 0 && totalCount > 0) {
      const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
      departmentPositionsPage.value = lastPage;
      await loadDepartmentPositions(departmentId);
      return;
    }

    departmentPositions.value = results.map(mapApiPositionToDepartmentCardRow);
    departmentPositionsTotal.value = totalCount;
  } catch (error) {
    if (requestId !== departmentPositionsRequestId) return;
    console.error("Error loading department positions:", error);
    departmentPositions.value = [];
    departmentPositionsTotal.value = 0;
  } finally {
    await nextTick();
    logPaneViewRightHeight();
  }
};

const loadOrphanPositions = async () => {
  const accountID = localStorage.getItem("currentAccountID");
  if (!accountID) {
    orphanPositions.value = [];
    orphanPositionsTotal.value = 0;
    return;
  }

  try {
    const pageSize = getDepartmentPositionsPageSize();
    const offset = (orphanPositionsPage.value - 1) * pageSize;
    const { data } = await getOrphanPositions(accountID, {
      search: orphanPositionSearch.value,
      status: ["draft", "active"],
      limit: pageSize,
      offset,
    });
    const results = data?.results || [];
    const totalCount = data?.count ?? results.length;

    if (results.length === 0 && offset > 0 && totalCount > 0) {
      const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
      orphanPositionsPage.value = lastPage;
      await loadOrphanPositions();
      return;
    }

    orphanPositions.value = results.map(mapApiPositionToDepartmentCardRow);
    orphanPositionsTotal.value = totalCount;
  } catch (error) {
    console.error("Error loading orphan positions:", error);
    orphanPositions.value = [];
    orphanPositionsTotal.value = 0;
  } finally {
    await nextTick();
    logPaneViewRightHeight();
  }
};

const fetchTreeDepartmentsPage = async (offset = 0) => {
  const accountID = localStorage.getItem("currentAccountID");
  if (!accountID) {
    return { results: [], count: 0 };
  }

  const { data } = await getListOfDepartments(
    accountID,
    "",
    { status: ["draft", "active"], ordering: DEPARTMENT_LIST_ORDERING },
    { limit: TREE_PAGE_SIZE, offset }
  );
  return normalizeDepartmentListResponse(data);
};

const rebuildTreeDepartments = async ({ suppressLoader = false } = {}) => {
  treeDepartmentsLoadingMore.value = false;
  const showLoader = !suppressLoader && treeDepartmentRows.value.length === 0;
  if (showLoader) {
    proxy?.$loader?.show?.();
  }

  try {
    const { results, count } = await fetchTreeDepartmentsPage(0);
    treeDepartmentRows.value = results;
    treeDepartmentsTotal.value = count;
  } catch (error) {
    console.error("Error loading tree departments:", error);
    treeDepartmentRows.value = [];
    treeDepartmentsTotal.value = 0;
  } finally {
    if (showLoader) {
      proxy?.$loader?.hide?.();
    }
    await nextTick();
    logPaneViewRightHeight();
  }
};

const loadMoreTreeDepartments = async () => {
  if (treeDepartmentsLoadingMore.value) return;
  if (treeDepartmentRows.value.length === 0) return;
  if (treeDepartmentRows.value.length >= treeDepartmentsTotal.value) return;

  treeDepartmentsLoadingMore.value = true;
  try {
    const offset = treeDepartmentRows.value.length;
    const { results, count } = await fetchTreeDepartmentsPage(offset);
    const existingIds = new Set(treeDepartmentRows.value.map((dept) => dept.id));
    const nextResults = results.filter((dept) => !existingIds.has(dept.id));
    treeDepartmentRows.value = [...treeDepartmentRows.value, ...nextResults];
    treeDepartmentsTotal.value = count;
  } catch (error) {
    console.error("Error loading more tree departments:", error);
  } finally {
    treeDepartmentsLoadingMore.value = false;
  }
};

const handleTreeScroll = (e) => {
  const el = paneLeftRef.value;
  if (!el || treeDepartmentsLoadingMore.value) return;
  if (treeDepartmentRows.value.length === 0) return;
  if (treeDepartmentRows.value.length >= treeDepartmentsTotal.value) return;
  if (e?.target && !el.contains(e.target)) return;

  const { scrollTop, scrollHeight, clientHeight } = el;
  const threshold = 150;
  if (scrollHeight - (scrollTop + clientHeight) < threshold) {
    loadMoreTreeDepartments();
  }
};

const focusLeftScrollContainer = () => {
  paneLeftRef.value?.focus?.({ preventScroll: true });
};

const handleTreeWheel = (e) => {
  const el = paneLeftRef.value;
  if (!el || !el.contains(e.target)) return;
  e.preventDefault();
  el.scrollTop = Math.max(0, Math.min(el.scrollTop + e.deltaY, el.scrollHeight - el.clientHeight));
};

const attachLeftPanelWheelListener = () => {
  removeLeftPanelWheelListener();
  const el = paneLeftRef.value;
  if (!el) return;
  el.addEventListener("wheel", handleTreeWheel, { passive: false });
  wheelListenerEl = el;
};

const removeLeftPanelWheelListener = () => {
  if (!wheelListenerEl) return;
  wheelListenerEl.removeEventListener("wheel", handleTreeWheel);
  wheelListenerEl = null;
};

const logPaneViewRightHeight = () => {
  const el = paneViewRightRef.value;
  if (!el) return;
  paneViewRightHeight.value = el.getBoundingClientRect().height;
};

const attachPaneViewRightHeightObserver = () => {
  detachPaneViewRightHeightObserver();
  const el = paneViewRightRef.value;
  if (!el) return;
  logPaneViewRightHeight();
  paneViewRightResizeObserver = new ResizeObserver(() => {
    logPaneViewRightHeight();
  });
  paneViewRightResizeObserver.observe(el);
};

const detachPaneViewRightHeightObserver = () => {
  if (paneViewRightResizeObserver) {
    paneViewRightResizeObserver.disconnect();
    paneViewRightResizeObserver = null;
  }
};

const ensurePaneViewHeightSync = async () => {
  if (viewSelection.value !== "tree-with-positions" && viewSelection.value !== "orphan-positions")
    return;
  await nextTick();
  attachPaneViewRightHeightObserver();
  logPaneViewRightHeight();
  await nextTick();
  attachLeftPanelWheelListener();
};

const selectFirstDepartment = () => {
  const first = departmentTreeData.value[0];
  if (!first) {
    selectedDepartment.value = null;
    return;
  }
  handleDepartmentTreeSelect({
    id: first.id,
    type: first.type,
    name: first.name,
    data: first,
  });
};

/** Orphan view: highlight first root department and expand it when it has sub-departments. */
const expandFirstDepartmentInTree = async () => {
  await nextTick();
  await nextTick();
  const first = departmentTreeData.value[0];
  if (!first?.id) {
    selectedDepartment.value = null;
    return;
  }
  const hasChildren = Array.isArray(first.children) && first.children.length > 0;
  selectedDepartment.value = {
    id: first.id,
    type: first.type || "department",
    name: first.name,
    code: first.code || "",
  };
  if (!departmentsSearchNavigateRef.value?.applyDepartmentSelectionExpand) {
    await nextTick();
  }
  departmentsSearchNavigateRef.value?.applyDepartmentSelectionExpand?.(first.id, {
    expandSelf: hasChildren,
  });
};

/** Selecting a department always opens the scope toggle on "Including sub-departments". */
const resetDepartmentPositionsScope = () => {
  if (departmentPositionsScope.value) {
    departmentPositionsScope.value = DEPARTMENT_POSITIONS_SCOPE_SUBTREE;
  }
};

const handleDepartmentTreeSelect = async (item) => {
  if (!item?.id) return;
  selectedDepartment.value = {
    id: item.id,
    type: item.type || "department",
    name: item.name,
    code: item.data?.code || item.code || "",
  };
  departmentPositionsPage.value = 1;
  resetDepartmentPositionsScope();
  selectedDepartmentDetail.value = null;
  departmentContextFiles.value = [];
  await Promise.all([loadDepartmentDetail(item.id), loadDepartmentPositions(item.id)]);
  departmentsSearchNavigateRef.value?.applyDepartmentSelectionExpand?.(item.id, {
    expandSelf: Array.isArray(item.data?.children) && item.data.children.length > 0,
  });
  await nextTick();
  logPaneViewRightHeight();
};

function pointerInPaneRight(clientX, clientY) {
  const el = paneViewRightRef.value;
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
}

function pointerInPaneLeft(clientX, clientY) {
  const el = paneLeftRef.value;
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
}

function getPositionDragCursorColors(state) {
  const fill = state === "Allowed" ? "#DBFAE6" : state === "Blocked" ? "#D92D20" : "#ffffff";
  const labelColor = state === "Blocked" ? "#D92D20" : "#101828";
  return { fill, labelColor };
}

function applyPositionDragCursorVars(state) {
  const { fill, labelColor } = getPositionDragCursorColors(state);
  document.documentElement.style.setProperty("--position-tree-drag-hand-fill", fill);
  document.documentElement.style.setProperty("--position-tree-drag-label-color", labelColor);

  const ghost = document.querySelector("[data-position-department-drag-ghost]");
  if (!ghost) return;

  ghost.style.setProperty("--position-tree-drag-hand-fill", fill);
  ghost.style.setProperty("--position-tree-drag-label-color", labelColor);
  ghost.querySelectorAll("[data-position-drag-ghost-hand-fill]").forEach((path) => {
    path.setAttribute("fill", fill);
  });
  const label = ghost.querySelector("[data-position-drag-ghost-label]");
  if (label instanceof HTMLElement) {
    label.style.color = labelColor;
  }
}

function setPositionTreeDragCursorVars(state) {
  applyPositionDragCursorVars(state);
}

function clearPositionTreeDragCursorVars() {
  document.documentElement.style.removeProperty("--position-tree-drag-hand-fill");
  document.documentElement.style.removeProperty("--position-tree-drag-label-color");
  const ghost = document.querySelector("[data-position-department-drag-ghost]");
  if (!ghost) return;
  ghost.style.removeProperty("--position-tree-drag-hand-fill");
  ghost.style.removeProperty("--position-tree-drag-label-color");
}

function setPositionDragAttachUiActive(active, { preserveDragCursorVars = false } = {}) {
  if (isPositionToDepartmentDragging.value === active) {
    if (!active) {
      document.documentElement.classList.remove("checkpoint-drag-cursor-active");
      document.body.classList.remove("checkpoint-drag-cursor-active");
      document.documentElement.style.cursor = "";
      document.body.style.cursor = "";
    }
    return;
  }

  const transparentCursor =
    'url(\'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/%3E\') 0 0, none';
  if (active) {
    isPositionToDepartmentDragging.value = true;
    positionDragCursorState.value = "Dragging";
    setPositionTreeDragCursorVars("Dragging");
    document.documentElement.classList.add("checkpoint-drag-cursor-active");
    document.body.classList.add("checkpoint-drag-cursor-active");
    document.documentElement.style.cursor = transparentCursor;
    document.body.style.cursor = transparentCursor;
  } else {
    isPositionToDepartmentDragging.value = false;
    document.documentElement.classList.remove("checkpoint-drag-cursor-active");
    document.body.classList.remove("checkpoint-drag-cursor-active");
    document.documentElement.style.cursor = "";
    document.body.style.cursor = "";
    if (!preserveDragCursorVars) {
      clearPositionTreeDragCursorVars();
    }
  }
}

function handlePositionDragOverState({ allowed }) {
  if (allowed == null) {
    if (positionDragCursorState.value === "Dragging") return;
    positionDragCursorState.value = "Dragging";
    setPositionTreeDragCursorVars("Dragging");
    return;
  }

  const next = allowed ? "Allowed" : "Blocked";
  if (positionDragCursorState.value === next) return;
  positionDragCursorState.value = next;
  setPositionTreeDragCursorVars(next);
}

function updatePaneDragCursorPosition(clientX, clientY) {
  const el = paneDragCursorRef.value;
  if (!el) return;
  const x = clientX - paneDragCursorLeftOffset + paneDragCursorLeftInsetPx;
  const y = clientY - 12 + paneDragCursorTopInsetPx;
  el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
}

function handlePositionDragStartFromTable({
  positionId,
  sourceDepartmentId,
  positionName,
  positionCodeStr,
  clientX = 0,
  clientY = 0,
  deferAttachUi = false,
}) {
  if (positionDragSessionActive.value || positionDragEndCleanup) {
    handlePositionDragEndFromTable();
  }

  positionDragSessionActive.value = true;
  positionDragSourceDepartmentId = sourceDepartmentId ?? null;
  positionDragSource.value = { sourceDepartmentId, positionId, positionName, positionCodeStr };
  updatePaneDragCursorPosition(clientX, clientY);
  setPositionDragAttachUiActive(!deferAttachUi, {
    preserveDragCursorVars: deferAttachUi,
  });
  if (deferAttachUi) {
    positionDragCursorState.value = "Dragging";
    setPositionTreeDragCursorVars("Dragging");
  }

  let dragCursorRafId = 0;
  let lastDragPointerEvent = null;
  const flushDragCursor = () => {
    dragCursorRafId = 0;
    const e = lastDragPointerEvent;
    if (!e) return;
    updatePaneDragCursorPosition(e.clientX, e.clientY);
    if (deferAttachUi) {
      const showAttachCursor = pointerInPaneLeft(e.clientX, e.clientY);
      setPositionDragAttachUiActive(showAttachCursor, {
        preserveDragCursorVars: !showAttachCursor,
      });
    } else if (pointerInPaneRight(e.clientX, e.clientY)) {
      if (isPositionToDepartmentDragging.value) {
        setPositionDragAttachUiActive(false, { preserveDragCursorVars: true });
      }
      if (positionDragCursorState.value !== "Dragging") {
        positionDragCursorState.value = "Dragging";
        setPositionTreeDragCursorVars("Dragging");
      }
    } else if (!isPositionToDepartmentDragging.value) {
      setPositionDragAttachUiActive(true);
    }
  };
  const onDragover = (e) => {
    lastDragPointerEvent = e;
    if (!dragCursorRafId) {
      dragCursorRafId = requestAnimationFrame(flushDragCursor);
    }
  };
  const onEnd = () => {
    if (dragCursorRafId) cancelAnimationFrame(dragCursorRafId);
    positionDragSessionActive.value = false;
    positionDragSourceDepartmentId = null;
    positionDragSource.value = null;
    setPositionDragAttachUiActive(false);
    clearStuckPositionDragArtifacts();
    document.removeEventListener("dragover", onDragover);
    document.removeEventListener("dragend", onEnd);
    document.removeEventListener("drop", onDrop, true);
    positionDragEndCleanup = null;
  };
  const onDrop = () => {
    window.requestAnimationFrame(() => {
      if (positionDragSessionActive.value) {
        handlePositionDragEndFromTable();
      }
    });
  };
  document.addEventListener("dragover", onDragover);
  document.addEventListener("dragend", onEnd);
  document.addEventListener("drop", onDrop, true);
  positionDragEndCleanup = onEnd;
}

function handlePositionDragEndFromTable() {
  positionDragDepartmentCardRef.value?.finishPositionDragSession?.({ notifyParent: false });
  if (positionDragEndCleanup) {
    positionDragEndCleanup();
    return;
  }
  positionDragSessionActive.value = false;
  positionDragSourceDepartmentId = null;
  positionDragSource.value = null;
  setPositionDragAttachUiActive(false);
  clearStuckPositionDragArtifacts();
}

function formatPositionAttachDialogLabel({ positionCodeStr, positionName, positionId }) {
  const code = String(positionCodeStr || "").trim();
  const name = String(positionName || "").trim();
  if (code) return code;
  if (name) return name;
  return positionId != null ? `Position #${positionId}` : "";
}

function handlePositionDroppedOnDepartmentTree(payload) {
  if (!payload?.positionId || payload.targetDepartmentId == null) return;

  const positionLabel = formatPositionAttachDialogLabel({
    positionCodeStr: payload.positionCodeStr,
    positionName: payload.positionName,
    positionId: payload.positionId,
  });
  const departmentLabel =
    payload.targetDepartmentName || `Department #${payload.targetDepartmentId}`;

  positionDragDepartmentCardRef.value?.finishPositionDragSession?.();
  if (positionDragSessionActive.value) {
    handlePositionDragEndFromTable();
  }
  clearPositionTreeDragCursorVars();

  pendingPositionAttach.value = {
    positionId: payload.positionId,
    sourceDepartmentId: payload.sourceDepartmentId,
    sourceDepartmentName:
      payload.sourceDepartmentId != null ? selectedDepartmentCardProps.value?.name || "" : "",
    targetDepartmentId: payload.targetDepartmentId,
    targetDepartmentName: payload.targetDepartmentName || "",
    positionName: payload.positionName || "",
    positionCodeStr: payload.positionCodeStr || "",
    positionLabel,
    targetDepartmentLabel: departmentLabel,
  };
  showDropToAttachPositionDialog.value = true;
}

function handleCloseDropToAttachPositionDialog() {
  showDropToAttachPositionDialog.value = false;
  pendingPositionAttach.value = null;
}

function onDropToAttachPositionDialogInput(open) {
  if (!open) handleCloseDropToAttachPositionDialog();
}

async function handleConfirmDropToAttachPosition() {
  const pending = pendingPositionAttach.value;
  if (!pending) return;
  const accountId = localStorage.getItem("currentAccountID");
  if (!accountId || pending.positionId == null || pending.targetDepartmentId == null) return;

  const sourceDepartmentId = pending.sourceDepartmentId;
  const targetDepartmentId = pending.targetDepartmentId;
  const positionLabel = pending.positionLabel;
  const departmentLabel = pending.targetDepartmentLabel;
  const isMoveBetweenDepartments = sourceDepartmentId != null;

  try {
    proxy?.$loader?.show();
    showDropToAttachPositionDialog.value = false;

    if (isMoveBetweenDepartments) {
      await movePositionToDepartment(accountId, sourceDepartmentId, {
        position_id: pending.positionId,
        destination_department: targetDepartmentId,
      });
    } else {
      await assignPositionToDepartment(accountId, targetDepartmentId, pending.positionId);
    }

    pendingPositionAttach.value = null;

    reloadDepartments();

    await refreshPaneTreeIfActive();
    await loadOrphanPositions();

    if (
      selectedDepartment.value &&
      sourceDepartmentId != null &&
      String(selectedDepartment.value.id) === String(sourceDepartmentId)
    ) {
      await Promise.all([
        loadDepartmentDetail(selectedDepartment.value.id),
        loadDepartmentPositions(selectedDepartment.value.id),
      ]);
    }

    const targetNode = findDepartmentNodeInTree(departmentTreeData.value, targetDepartmentId);
    if (targetNode) {
      await handleDepartmentTreeSelect({
        id: targetNode.id,
        type: targetNode.type || "department",
        name: targetNode.name,
        data: targetNode,
      });
    }

    if (isMoveBetweenDepartments) {
      proxy?.$notifications?.success(
        "Position successfully moved",
        `Position ${positionLabel} was moved to department ${departmentLabel}`,
        5000
      );
    } else {
      proxy?.$notifications?.success(`${positionLabel} assigned to ${departmentLabel}.`, "", 5000);
    }
  } catch (error) {
    console.error(
      isMoveBetweenDepartments
        ? "Error moving position to department:"
        : "Error attaching position to department:",
      error
    );
    pendingPositionAttach.value = null;
    const errorCode =
      error?.response?.data?.detail ||
      error?.response?.data?.error ||
      error?.response?.data?.[0] ||
      "";
    const ERROR_MESSAGES = {
      DEPARTMENT_MOVE_REQUIRES_ATTACHED:
        "Position must be assigned to a department before it can be moved.",
      DEPARTMENT_ASSIGN_REQUIRES_DRAFT_OR_ACTIVE:
        "The destination department must be Draft or Active to receive a position.",
      DEPARTMENT_ASSIGN_REQUIRES_POSITION_DRAFT_OR_ACTIVE:
        "Only Draft or Active positions can be moved to a department.",
    };
    const specificMessage = ERROR_MESSAGES[errorCode];
    proxy?.$notifications?.error(
      specificMessage ||
        (isMoveBetweenDepartments
          ? "Failed to move position to department"
          : "Failed to attach position to department")
    );
  } finally {
    proxy?.$loader?.hide();
  }
}

async function handleDepartmentPositionReorder({ data: reorderedPageRows }) {
  const departmentId = selectedDepartment.value?.id;
  const accountId = localStorage.getItem("currentAccountID");
  if (!accountId || !departmentId || !reorderedPageRows?.length) return;

  const previousPositions = [...departmentPositions.value];

  try {
    proxy?.$loader?.show();

    const total = departmentPositionsTotal.value;
    if (!total) return;

    departmentPositions.value = reorderedPageRows.map((row) => {
      const existing = previousPositions.find((position) => position.id === row.id);
      return existing ? { ...existing, ...row } : row;
    });

    let positionIds;
    // The reorder endpoint rejects ids outside the department, and subtree scope also lists
    // sub-department positions, so only directly owned ids are submitted.
    const isOwnPosition = (positionDepartmentId) =>
      positionDepartmentId == null || String(positionDepartmentId) === String(departmentId);

    if (total <= reorderedPageRows.length) {
      positionIds = reorderedPageRows
        .filter((row) => isOwnPosition(row.departmentId))
        .map((row) => row.id);
    } else {
      const { data } = await getPositionsForDepartment(accountId, departmentId, {
        search: departmentPositionSearch.value,
        status: convertDepartmentPositionFiltersToApi(departmentPositionFilterChips.value),
        archived: resolveDepartmentPositionArchivedFilter(departmentPositionFilterChips.value),
        limit: total,
        offset: 0,
        departmentScope: departmentPositionsScope.value,
      });
      const fullResults = data?.results || [];
      const fullIds = fullResults.map((position) => position.id);
      const ownIds = new Set(
        fullResults
          .filter((position) => isOwnPosition(position.department_id))
          .map((position) => position.id)
      );
      const perPage = getDepartmentPositionsPageSize();
      const offset = (departmentPositionsPage.value - 1) * perPage;
      const reorderedPageIds = reorderedPageRows.map((row) => row.id);
      const merged = [
        ...fullIds.slice(0, offset),
        ...reorderedPageIds,
        ...fullIds.slice(offset + reorderedPageIds.length),
      ];

      if (merged.length !== fullIds.length) {
        await loadDepartmentPositions(departmentId);
        return;
      }
      positionIds = merged.filter((id) => ownIds.has(id));
    }

    await reorderPositionsInDepartment(accountId, departmentId, {
      position_ids: positionIds,
    });

    proxy?.$notifications?.success("Positions reordered", "", 3000);
    await loadDepartmentPositions(departmentId);
  } catch (error) {
    console.error("Error reordering department positions:", error);
    departmentPositions.value = previousPositions;
    proxy?.$notifications?.error("Failed to reorder positions");
    await loadDepartmentPositions(departmentId);
  } finally {
    proxy?.$loader?.hide();
  }
}

function handleOrphanPositionReorder({ data: reorderedPageRows }) {
  if (!reorderedPageRows?.length) return;
  orphanPositions.value = [...reorderedPageRows];
}

function getPositionMenuItemsForRow(row) {
  if (!row) return [];
  return dropdownItems.getOrgObjectDropdownItems({
    status: row.menuStatus || String(row.status || "").toLowerCase(),
    name: row.name,
    assigned_primary: row.assigned_primary,
    archived: row.archived,
  });
}

function getActivePositionAccountId() {
  return localStorage.getItem("currentAccountID");
}

async function refreshPositionsAfterMenuAction() {
  const departmentId = selectedDepartment.value?.id;
  await Promise.all([
    loadOrphanPositions(),
    departmentId ? loadDepartmentPositions(departmentId) : Promise.resolve(),
    departmentId ? loadDepartmentDetail(departmentId) : Promise.resolve(),
    refreshPaneTreeIfActive(),
  ]);
}

function handlePositionCardMenu({ position, item }) {
  if (!position?.id || !item?.value) return;
  activePosition.value = { ...position };
  switch (item.value) {
    case "activate_action":
      showActivatePositionDialog.value = true;
      break;
    case "delete_action":
      showDeletePositionDialog.value = true;
      break;
    case "rename_action":
      renamePositionError.value = "";
      showRenamePositionDialog.value = true;
      break;
    case "attach_position_role":
      handleOpenAttachRoleToPosition();
      break;
    case "archive_action":
      positionArchiveAction.value = "archive";
      archivePositionError.value = "";
      showArchivePositionDialog.value = true;
      break;
    case "unarchive_action":
      positionArchiveAction.value = "unarchive";
      archivePositionError.value = "";
      showArchivePositionDialog.value = true;
      break;
    case "retire_action":
      showRetirePositionDialog.value = true;
      break;
    case "reactivate_action":
      showReactivatePositionDialog.value = true;
      break;
    case "cloned_action":
      showClonePositionDialog.value = true;
      break;
    default:
      break;
  }
}

function mapAttachableRoleOption(item) {
  const code = item.code || item.role_code || item.code_str?.match(/\d+/)?.[0] || "";
  return {
    ...item,
    role_code: code,
    code,
    attached: item.attached_to_position || false,
    disabled: item.attached_to_position || false,
  };
}

async function fetchAttachableRolesForPositionList(isLoadMore = false) {
  const accountId = getActivePositionAccountId();
  const positionId = activePosition.value?.id;
  if (!accountId || !positionId) return;

  try {
    if (isLoadMore) attachableRolesLoadingMore.value = true;
    const offset = (attachableRolesPage.value - 1) * ATTACHABLE_ROLES_PAGE_SIZE;
    const { data } = await fetchAttachableRolesForPosition(
      accountId,
      positionId,
      ATTACHABLE_ROLES_PAGE_SIZE,
      offset,
      attachableRolesSearch.value
    );
    const mapped = (data?.results || []).map(mapAttachableRoleOption);
    attachableRolesForPosition.value = isLoadMore
      ? [...attachableRolesForPosition.value, ...mapped]
      : mapped;
    attachableRolesHasMore.value = Boolean(data?.next);
  } catch (error) {
    console.error("Error fetching attachable roles for position:", error);
    attachableRolesForPosition.value = [];
    attachableRolesHasMore.value = false;
  } finally {
    attachableRolesLoadingMore.value = false;
  }
}

async function handleOpenAttachRoleToPosition() {
  showAttachRoleToPositionDialog.value = true;
  attachRoleOpenDefault.value = true;
  attachableRolesPage.value = 1;
  attachableRolesSearch.value = "";
  selectedAttachRoleId.value = null;
  await fetchAttachableRolesForPositionList(false);
}

function handleAttachRoleSelected(role) {
  selectedAttachRoleId.value = role?.id ?? null;
}

async function loadMoreAttachableRolesForPosition() {
  attachableRolesPage.value += 1;
  await fetchAttachableRolesForPositionList(true);
}

async function searchAttachableRolesForPosition(searchTerm) {
  attachableRolesSearch.value = searchTerm || "";
  attachableRolesPage.value = 1;
  await fetchAttachableRolesForPositionList(false);
}

async function handleAttachRoleToPositionSubmit() {
  const accountId = getActivePositionAccountId();
  const positionId = activePosition.value?.id;
  if (!accountId || !positionId || !selectedAttachRoleId.value) return;

  try {
    attachRoleAssigning.value = true;
    proxy?.$loader?.show();
    await attachRoleToPosition(accountId, positionId, {
      attachment_id: selectedAttachRoleId.value,
    });
    showAttachRoleToPositionDialog.value = false;
    selectedAttachRoleId.value = null;
    proxy?.$notifications?.success("Role attached to position", "", 3000);
    await refreshPositionsAfterMenuAction();
  } catch (error) {
    console.error("Error attaching role to position:", error);
    proxy?.$notifications?.error("Failed to attach role to position");
  } finally {
    attachRoleAssigning.value = false;
    proxy?.$loader?.hide();
  }
}

async function handleAttachNewRoleToPosition(roleData) {
  const accountId = getActivePositionAccountId();
  if (!accountId || !roleData?.name) return;

  try {
    proxy?.$loader?.show();
    const { data } = await createRole(accountId, { name: roleData.name, account: accountId });
    if (data?.id) {
      selectedAttachRoleId.value = data.id;
      await handleAttachRoleToPositionSubmit();
    }
  } catch (error) {
    console.error("Error creating role for position:", error);
    proxy?.$notifications?.error("Failed to create and attach role");
  } finally {
    proxy?.$loader?.hide();
  }
}

async function handleRenamePositionSubmit(newName) {
  const accountId = getActivePositionAccountId();
  const positionId = activePosition.value?.id;
  if (!accountId || !positionId || !newName?.trim()) return;

  renamePositionError.value = "";
  try {
    proxy?.$loader?.show();
    await updatePosition(accountId, positionId, {
      name: newName.trim(),
      account: accountId,
    });
    showRenamePositionDialog.value = false;
    proxy?.$notifications?.success("Position renamed", "", 3000);
    await refreshPositionsAfterMenuAction();
  } catch (error) {
    // MDP-608: Department-card rows keep the dialog open with the error inline.
    renamePositionError.value = positionActionErrorMessage(error, "rename");
    console.error("Error renaming position:", error);
  } finally {
    proxy?.$loader?.hide();
  }
}

async function handleClonePositionSubmit() {
  const accountId = getActivePositionAccountId();
  const position = activePosition.value;
  if (!accountId || !position?.id) return;

  const sourceName = String(position.name || "").trim();
  if (!sourceName) return;

  try {
    proxy?.$loader?.show();
    const { data: clonedPosition } = await createPosition(accountId, {
      name: `Clone of ${sourceName}`,
      account: accountId,
    });
    if (clonedPosition?.id) {
      const { data: rolesData } = await getListOfRolesAttachedToPosition(
        accountId,
        100,
        0,
        "",
        position.id,
        {}
      );
      const attachedRoles = rolesData?.results || [];
      for (const role of attachedRoles) {
        if (!role?.id) continue;
        await attachRoleToPosition(accountId, clonedPosition.id, {
          attachment_id: role.id,
        });
      }
    }
    showClonePositionDialog.value = false;
    proxy?.$notifications?.success(
      "Position successfully cloned",
      `Position Clone of ${sourceName} was successfully created.`,
      5000
    );
    await refreshPositionsAfterMenuAction();
  } catch (error) {
    console.error("Error cloning position:", error);
    proxy?.$notifications?.error("Failed to clone position");
  } finally {
    proxy?.$loader?.hide();
  }
}

async function handleRetirePositionSubmit() {
  const accountId = getActivePositionAccountId();
  const positionId = activePosition.value?.id;
  if (!accountId || !positionId) return;

  try {
    proxy?.$loader?.show();
    await retirePosition(accountId, positionId);
    showRetirePositionDialog.value = false;
    proxy?.$notifications?.success("Position retired", "", 3000);
    await refreshPositionsAfterMenuAction();
  } catch (error) {
    console.error("Error retiring position:", error);
    proxy?.$notifications?.error("Failed to retire position");
  } finally {
    proxy?.$loader?.hide();
  }
}

async function handleReactivatePositionSubmit() {
  const accountId = getActivePositionAccountId();
  const positionId = activePosition.value?.id;
  if (!accountId || !positionId) return;

  try {
    proxy?.$loader?.show();
    await reActivatePosition(accountId, positionId);
    showReactivatePositionDialog.value = false;
    proxy?.$notifications?.success("Position reactivated", "", 3000);
    await refreshPositionsAfterMenuAction();
  } catch (error) {
    console.error("Error reactivating position:", error);
    proxy?.$notifications?.error("Failed to reactivate position");
  } finally {
    proxy?.$loader?.hide();
  }
}

async function handleArchivePositionSubmit() {
  const accountId = getActivePositionAccountId();
  const positionId = activePosition.value?.id;
  if (!accountId || !positionId) return;

  const action = positionArchiveAction.value;
  archivePositionError.value = "";
  try {
    proxy?.$loader?.show();
    if (action === "archive") {
      await archivePosition(accountId, "position", positionId);
    } else {
      await unArchivePosition(accountId, "position", positionId);
    }
    showArchivePositionDialog.value = false;
    proxy?.$notifications?.success(
      action === "archive" ? "Position archived" : "Position unarchived",
      "",
      3000
    );
    await refreshPositionsAfterMenuAction();
  } catch (error) {
    // MDP-608: keep the archive/unarchive dialog open; no generic toast.
    archivePositionError.value = positionActionErrorMessage(error, action);
    console.error("Error updating position archive state:", error);
  } finally {
    proxy?.$loader?.hide();
  }
}

async function handleActivatePositionSubmit() {
  const accountId = getActivePositionAccountId();
  const positionId = activePosition.value?.id;
  if (!accountId || !positionId) return;

  try {
    proxy?.$loader?.show();
    await activatePosition(accountId, positionId);
    showActivatePositionDialog.value = false;
    proxy?.$notifications?.success("Position activated", "", 3000);
    await refreshPositionsAfterMenuAction();
  } catch (error) {
    console.error("Error activating position:", error);
    proxy?.$notifications?.error("Failed to activate position");
  } finally {
    proxy?.$loader?.hide();
  }
}

async function handleDeletePositionSubmit() {
  const accountId = getActivePositionAccountId();
  const position = activePosition.value;
  if (!accountId || !position?.id) return;

  const positionName = String(position.name || "").trim();

  try {
    proxy?.$loader?.show();
    await deletePosition(accountId, position.id);
    showDeletePositionDialog.value = false;
    activePosition.value = null;
    proxy?.$notifications?.success(
      "Position successfully deleted",
      positionName
        ? `Position ${positionName} was successfully deleted`
        : "The position was successfully deleted.",
      5000
    );
    await refreshPositionsAfterMenuAction();
  } catch (error) {
    console.error("Error deleting position:", error);
    proxy?.$notifications?.error("Failed to delete position");
  } finally {
    proxy?.$loader?.hide();
  }
}

function formatPositionReleaseLabel(position) {
  const code = position?.code ? `#${position.code}` : "";
  const name = String(position?.name || "").trim();
  if (code && name) return `${code} - ${name}`;
  if (name) return name;
  if (code) return code;
  return position?.id != null ? `Position #${position.id}` : "";
}

function handleRemovePositionFromDepartment(position) {
  if (!position?.id || !selectedDepartment.value?.id) return;
  positionToRelease.value = {
    id: position.id,
    label: formatPositionReleaseLabel(position),
    departmentId: selectedDepartment.value.id,
  };
  showReleasePositionDialog.value = true;
}

async function handleConfirmReleasePosition() {
  const pending = positionToRelease.value;
  if (!pending?.id || !pending.departmentId) return;
  const accountId = localStorage.getItem("currentAccountID");
  if (!accountId) return;

  try {
    proxy?.$loader?.show();
    showReleasePositionDialog.value = false;
    await releasePositionFromDepartment(accountId, pending.departmentId, {
      position_id: pending.id,
    });

    const positionLabel = pending.label;
    const departmentLabel = selectedDepartmentCardProps.value?.name || "";
    positionToRelease.value = null;

    reloadDepartments();

    await refreshPaneTreeIfActive();
    await loadOrphanPositions();
    if (selectedDepartment.value) {
      await Promise.all([
        loadDepartmentDetail(selectedDepartment.value.id),
        loadDepartmentPositions(selectedDepartment.value.id),
      ]);
    }

    proxy?.$notifications?.success(
      "Position successfully released",
      `Position ${positionLabel} was released from department ${departmentLabel}`,
      5000
    );
  } catch (error) {
    console.error("Error releasing position from department:", error);
    proxy?.$notifications?.error("Failed to release position from department");
  } finally {
    proxy?.$loader?.hide();
  }
}

const handleTreeViewRowClick = async (dept) => {
  if (!dept?.id) return;
  selectedDepartment.value = {
    id: dept.id,
    type: "department",
    name: dept.name,
    code: dept.code || "",
  };
  await handleViewSelectionChange("tree-with-positions", { preserveSelection: true });
  await nextTick();
  const targetNode = findDepartmentNodeInTree(departmentTreeData.value, dept.id);
  departmentsSearchNavigateRef.value?.applyDepartmentSelectionExpand?.(dept.id, {
    expandSelf: Array.isArray(targetNode?.children) && targetNode.children.length > 0,
  });
};

/**
 * ER-11885 — "Departments" breadcrumb on the Department detail. The detail is an
 * in-component drill-down rendered at the same `/organization/departments` route as
 * the list, so ExBreadcrumbs' href cannot navigate (fullPath already equals it).
 * Clear the selection and show the Departments list view again.
 */
const handleDepartmentCardBackToList = async () => {
  selectedDepartment.value = null;
  resetDepartmentPositionsScope();
  await handleViewSelectionChange("tree");
};

const handleViewSelectionChange = async (mode, { preserveSelection = false } = {}) => {
  if (mode !== "tree-with-positions" && mode !== "orphan-positions") {
    detachPaneViewRightHeightObserver();
    paneViewRightHeight.value = null;
  }

  viewSelection.value = mode;
  if (mode === "tree-with-positions") {
    await rebuildTreeDepartments();
    const selectedInTree =
      preserveSelection &&
      selectedDepartment.value &&
      findDepartmentInTree(departmentTreeData.value, selectedDepartment.value.id);

    if (selectedInTree) {
      await Promise.all([
        loadDepartmentDetail(selectedDepartment.value.id),
        loadDepartmentPositions(selectedDepartment.value.id),
      ]);
    } else if (departmentTreeData.value.length > 0) {
      selectFirstDepartment();
    } else {
      selectedDepartment.value = null;
    }
    await ensurePaneViewHeightSync();
  } else if (mode === "orphan-positions") {
    proxy?.$loader?.show?.();
    try {
      await rebuildTreeDepartments({ suppressLoader: true });
      orphanPositionsPage.value = 1;
      await loadOrphanPositions();
      await expandFirstDepartmentInTree();
      await ensurePaneViewHeightSync();
    } finally {
      await hideLoaderAfterRender();
    }
  }
};

const debouncedDepartmentPositionSearch = debounce(async () => {
  departmentPositionsPage.value = 1;
  await loadDepartmentPositions();
}, 300);

const handleDepartmentPositionSearch = (value) => {
  departmentPositionSearch.value = value || "";
  debouncedDepartmentPositionSearch();
};

const handleDepartmentPositionScopeChange = async (scope) => {
  const nextScope =
    scope === "self" ? DEPARTMENT_POSITIONS_SCOPE_DIRECT : DEPARTMENT_POSITIONS_SCOPE_SUBTREE;
  if (departmentPositionsScope.value === nextScope) return;
  departmentPositionsScope.value = nextScope;
  departmentPositionsPage.value = 1;
  await loadDepartmentPositions();
};

const handleDepartmentPositionRemoveFilter = async (key) => {
  departmentPositionFilterChips.value = departmentPositionFilterChips.value.filter(
    (chip) => chip.key !== key
  );
  departmentPositionsPage.value = 1;
  await loadDepartmentPositions();
};

const handleDepartmentPositionFiltersApplied = async (chips) => {
  departmentPositionFilterChips.value = chips || [];
  departmentPositionsPage.value = 1;
  await loadDepartmentPositions();
};

const handleDepartmentPositionClearFilters = async () => {
  departmentPositionSearch.value = "";
  departmentPositionFilterChips.value = [];
  departmentPositionsPage.value = 1;
  await loadDepartmentPositions();
};

const handleDepartmentPositionPageChange = async (page) => {
  departmentPositionsPage.value = page || 1;
  await loadDepartmentPositions();
};

const handleOrphanPositionPageChange = async (page) => {
  orphanPositionsPage.value = page || 1;
  await loadOrphanPositions();
};

const debouncedOrphanPositionSearch = debounce(async () => {
  orphanPositionsPage.value = 1;
  await loadOrphanPositions();
}, 300);

const handleOrphanPositionSearch = (query) => {
  orphanPositionSearch.value = query || "";
  debouncedOrphanPositionSearch();
};

const handleOrphanPositionClearFilters = async () => {
  orphanPositionSearch.value = "";
  orphanPositionsPage.value = 1;
  await loadOrphanPositions();
};

const handleDepartmentCardMenu = (item) => {
  if (!item?.value || !selectedDepartmentRecord.value) return;
  handleDepartmentAction({
    type: item.value,
    department: selectedDepartmentRecord.value,
  });
};

const handlePaneResizeMove = (event) => {
  if (!paneResizeActive || !paneViewRightRef.value?.parentElement) return;
  const layoutRect = paneViewRightRef.value.parentElement.getBoundingClientRect();
  const nextPercent = ((event.clientX - layoutRect.left) / layoutRect.width) * 100;
  paneLeftWidthPercent.value = Math.min(50, Math.max(25, nextPercent));
};

const handlePaneResizeEnd = () => {
  paneResizeActive = false;
  window.removeEventListener("mousemove", handlePaneResizeMove);
  window.removeEventListener("mouseup", handlePaneResizeEnd);
};

const handlePaneResizeStart = () => {
  paneResizeActive = true;
  window.addEventListener("mousemove", handlePaneResizeMove);
  window.addEventListener("mouseup", handlePaneResizeEnd);
};

const getDepartmentChatterMessage = (note) => {
  if (note === null || note === undefined) return "";
  const rawNote = String(note).trim();
  if (!rawNote) return "";

  if (rawNote.startsWith("{") || rawNote.startsWith("[")) {
    try {
      const parsed = JSON.parse(rawNote);
      if (parsed && typeof parsed.summary === "string" && parsed.summary.trim()) {
        return parsed.summary.trim();
      }
    } catch {
      // fall through to raw note
    }
  }

  return rawNote;
};

const transformChatterResults = (results = []) => {
  return results.map((el) => {
    const date = new Date(el.recorded_on);
    const millisecondsString = date.getTime().toString();
    const nameParts = el.recorded_by_name ? el.recorded_by_name.split(" ") : [""];

    return {
      id: el.id,
      avatar:
        nameParts.length >= 2
          ? {
              picture: "",
              firstName: nameParts[0],
              lastName: nameParts.slice(1).join(" "),
            }
          : {
              picture: "",
              firstName: el.recorded_by_name || "",
              lastName: "",
            },
      IsExIQtiveBot: false,
      message: getDepartmentChatterMessage(el.note),
      created_on: millisecondsString,
      IsHistory: el.is_history,
    };
  });
};

const loadChatterData = async (recordId, type, page = 1) => {
  const accountID = localStorage.getItem("currentAccountID");
  if (!accountID || !recordId || !type) return;

  const { data } = await getChatterList(accountID, recordId, type, page);

  if (data?.count != null) {
    totalChatterData.value = data.count;
  }

  if (data?.results?.length) {
    chatterData.value.push(...transformChatterResults(data.results));
  }
};

const openChatter = async (dept) => {
  if (!dept?.id) return;

  listData.value = {
    id: dept.id,
    department_code: dept.code || "",
    department_name: dept.name || "",
    status: dept.status,
  };
  typeValue.value = dept.name || "";
  typeName.value = "department";
  iconName.value = "org_icon_department";
  chatterRecordType.value = "department";
  drawer.value = true;

  chatterData.value = [];
  totalChatterData.value = 0;

  try {
    await loadChatterData(dept.id, "department", 1);
  } catch (error) {
    console.error("Error loading department chatter data:", error);
  }
};

const openPositionChatter = async (position) => {
  if (!position?.id) return;

  listData.value = {
    id: position.id,
    position_code: position.code || "",
    position_name: position.name || "",
    status: position.status,
  };
  typeValue.value = position.name || "";
  typeName.value = "position";
  iconName.value = "org_icon_position";
  chatterRecordType.value = "position";
  drawer.value = true;

  chatterData.value = [];
  totalChatterData.value = 0;

  try {
    await loadChatterData(position.id, "position", 1);
  } catch (error) {
    console.error("Error loading position chatter data:", error);
  }
};

const handleRequestMoreChatter = async (recordData) => {
  if (!recordData?.id || !chatterRecordType.value) return;

  try {
    const currentLength = chatterData.value.length;
    const page = Math.ceil(currentLength / 10) + 1;
    await loadChatterData(recordData.id, chatterRecordType.value, page);
  } catch (error) {
    console.error("Error loading more chatter data:", error);
  }
};

const closeDrawer = () => {
  drawer.value = false;
};

const getDepartmentStatus = (dept) => {
  if (!dept?.status) return "Draft";
  const status = String(dept.status).toLowerCase();
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const handleDepartmentAction = async (event) => {
  if (!event?.type) return;

  if (event.type === "activate") {
    activateDepartmentTarget.value = event.department || null;
    showActivateDepartmentDialog.value = true;
    return;
  }

  if (event.type === "retire") {
    retireDepartmentTarget.value = event.department || null;
    showRetireDepartmentDialog.value = true;
    return;
  }

  if (event.type === "delete") {
    deleteDepartmentTarget.value = event.department || null;
    showDeleteDepartmentDialog.value = true;
    return;
  }

  if (event.type === "rename") {
    clearDepartmentError();
    renameDepartmentTarget.value = event.department || null;
    showRenameDepartmentDialog.value = true;
    return;
  }

  if (event.type === "reactivate") {
    reactivateDepartmentTarget.value = event.department || null;
    showReactivateDepartmentDialog.value = true;
    return;
  }

  if (event.type === "archive") {
    archiveDepartmentTarget.value = event.department || null;
    showArchiveDepartmentDialog.value = true;
    return;
  }

  if (event.type === "unarchive") {
    const department = event.department || null;
    if (department) {
      unarchiveDepartment.start({ id: department.id, name: department.name || "" });
    }
    return;
  }

  emit("department-action", event);
};

const handleCloseActivateDepartmentDialog = () => {
  if (proxy?.$loader?.isAppLoading) return;
  showActivateDepartmentDialog.value = false;
  activateDepartmentTarget.value = null;
};

const handleActivateDepartmentSubmit = async () => {
  const dept = activateDepartmentTarget.value;
  if (!dept?.id) return;

  try {
    proxy?.$loader?.setAppLoading?.(true);
    const accountID = localStorage.getItem("currentAccountID");
    await activateDepartment(accountID, dept.id);
    showActivateDepartmentDialog.value = false;
    activateDepartmentTarget.value = null;
    await reloadDepartments();
  } catch (error) {
    console.error("Error activating department:", error);
    const apiError =
      normalizeApiErrorText(error?.response?.data?.detail) ||
      normalizeApiErrorText(error?.response?.data?.error) ||
      normalizeApiErrorText(error?.response?.data?.[0]) ||
      normalizeApiErrorText(error?.response?.data?.parent_department?.[0]);
    const message =
      apiError || "Cannot activate this department. Its parent department must be active.";
    proxy?.$notifications?.error(message);
    showActivateDepartmentDialog.value = false;
    activateDepartmentTarget.value = null;
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
};

const handleCloseRetireDepartmentDialog = () => {
  if (proxy?.$loader?.isAppLoading) return;
  showRetireDepartmentDialog.value = false;
  retireDepartmentTarget.value = null;
};

const handleRetireDepartmentSubmit = async () => {
  const dept = retireDepartmentTarget.value;
  if (!dept?.id) return;

  try {
    proxy?.$loader?.setAppLoading?.(true);
    const accountID = localStorage.getItem("currentAccountID");
    await retireDepartment(accountID, dept.id);
    showRetireDepartmentDialog.value = false;
    retireDepartmentTarget.value = null;
    await reloadDepartments();
  } catch (error) {
    console.error("Error retiring department:", error);
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
};

const handleCloseDeleteDepartmentDialog = () => {
  if (proxy?.$loader?.isAppLoading) return;
  showDeleteDepartmentDialog.value = false;
  deleteDepartmentTarget.value = null;
};

const handleDeleteDepartmentSubmit = async () => {
  const dept = deleteDepartmentTarget.value;
  if (!dept?.id) return;

  try {
    proxy?.$loader?.setAppLoading?.(true);
    const accountID = localStorage.getItem("currentAccountID");
    await deleteDepartmentApi(accountID, dept.id);
    showDeleteDepartmentDialog.value = false;
    deleteDepartmentTarget.value = null;
    await reloadDepartments();
  } catch (error) {
    console.error("Error deleting department:", error);
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
};

const handleCloseRenameDepartmentDialog = () => {
  if (proxy?.$loader?.isAppLoading) return;
  showRenameDepartmentDialog.value = false;
  renameDepartmentTarget.value = null;
  clearDepartmentError();
};

const handleRenameDepartmentSubmit = async (name) => {
  const dept = renameDepartmentTarget.value;
  const departmentName = typeof name === "string" ? name.trim() : "";
  if (!dept?.id || !departmentName) return;

  try {
    proxy?.$loader?.setAppLoading?.(true);
    clearDepartmentError();
    const accountID = localStorage.getItem("currentAccountID");
    await editDepartment(accountID, dept.id, { name: departmentName });
    showRenameDepartmentDialog.value = false;
    renameDepartmentTarget.value = null;
    await reloadDepartments();
  } catch (error) {
    console.error("Error renaming department:", error);
    const apiError = error.response?.data?.[0] || error.response?.data?.name?.[0];
    if (nameConflictMessage(error)) {
      departmentErrorMessage.value = NAME_IN_USE;
    } else if (apiError === "Same name already exists" || apiError?.includes?.("already exists")) {
      departmentErrorMessage.value = NAME_IN_USE;
    } else {
      departmentErrorMessage.value = apiError || "An error occurred while renaming the department.";
    }
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
};

const handleCloseReactivateDepartmentDialog = () => {
  if (proxy?.$loader?.isAppLoading) return;
  showReactivateDepartmentDialog.value = false;
  reactivateDepartmentTarget.value = null;
};

const handleReactivateDepartmentSubmit = async () => {
  const dept = reactivateDepartmentTarget.value;
  if (!dept?.id) return;

  try {
    proxy?.$loader?.setAppLoading?.(true);
    const accountID = localStorage.getItem("currentAccountID");
    await reActivateDepartment(accountID, dept.id);
    showReactivateDepartmentDialog.value = false;
    reactivateDepartmentTarget.value = null;
    await reloadDepartments();
  } catch (error) {
    console.error("Error reactivating department:", error);
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
};

const handleCloseArchiveDepartmentDialog = () => {
  if (proxy?.$loader?.isAppLoading) return;
  showArchiveDepartmentDialog.value = false;
  archiveDepartmentTarget.value = null;
};

const handleArchiveDepartmentSubmit = async () => {
  const dept = archiveDepartmentTarget.value;
  if (!dept?.id) return;

  try {
    proxy?.$loader?.setAppLoading?.(true);
    const accountID = localStorage.getItem("currentAccountID");
    await archiveDepartmentApi(accountID, dept.id);
    showArchiveDepartmentDialog.value = false;
    archiveDepartmentTarget.value = null;
    await reloadDepartments();
  } catch (error) {
    console.error("Error archiving department:", error);
  } finally {
    proxy?.$loader?.setAppLoading?.(false);
  }
};

const handleCloseUnarchiveDepartmentDialog = () => {
  if (proxy?.$loader?.isAppLoading) return;
  showUnarchiveDepartmentDialog.value = false;
  unarchiveDepartment.close();
};

const handleUnarchiveDepartmentSubmit = async () => {
  if (!unarchiveDepartmentTarget.value?.id) return;
  await unarchiveDepartment.confirm();
};

// Drag and Drop Handlers
const getSubtree = (deptId, depts) => {
  const index = depts.findIndex((d) => d.id === deptId);
  if (index === -1) return [];
  const rootLevel = depts[index].level || 0;
  const result = [depts[index]];
  for (let i = index + 1; i < depts.length; i++) {
    if ((depts[i].level || 0) > rootLevel) {
      result.push(depts[i]);
    } else {
      break;
    }
  }
  return result;
};

const getParentId = (depts, index) => {
  const level = depts[index]?.level ?? 0;
  if (level === 0) return null;
  for (let i = index - 1; i >= 0; i--) {
    if ((depts[i].level ?? 0) === level - 1) {
      return depts[i].id;
    }
  }
  return null;
};

// Backend `new_sequence` is the 0-based index within the sibling group, not the flat row index.
const getSiblingSequence = (depts, index) => {
  if (index < 0) return 0;
  const level = depts[index].level ?? 0;
  const parentId = getParentId(depts, index);
  let sequence = 0;
  for (let i = 0; i < index; i++) {
    if ((depts[i].level ?? 0) === level && getParentId(depts, i) === parentId) {
      sequence += 1;
    }
  }
  return sequence;
};

const areSiblingDepartments = (depts, sourceIndex, targetIndex) => {
  if (sourceIndex < 0 || targetIndex < 0) return false;
  const source = depts[sourceIndex];
  const target = depts[targetIndex];
  if (!source || !target) return false;
  return (
    (source.level ?? 0) === (target.level ?? 0) &&
    getParentId(depts, sourceIndex) === getParentId(depts, targetIndex)
  );
};

const canPromoteDepartmentToRoot = (draggedDept, targetDept) => {
  if (!draggedDept || !targetDept) return false;
  return (draggedDept.level ?? 0) > 0 && (targetDept.level ?? 0) === 0;
};

const canNestDepartmentInside = (draggedDept, targetDept) => {
  if (!draggedDept || !targetDept) return false;
  const draggedStatus = String(draggedDept.status || "").toLowerCase();
  const targetStatus = String(targetDept.status || "").toLowerCase();
  // An active department's parent must be active, so an active department
  // cannot be nested inside an inactive (draft/retired/archived) department.
  if (draggedStatus === "active" && targetStatus !== "active") return false;
  return true;
};

const canDropDepartmentBeforeOrAfter = (depts, sourceIndex, targetIndex) => {
  if (sourceIndex < 0 || targetIndex < 0) return false;
  const source = depts[sourceIndex];
  const target = depts[targetIndex];
  if (!source || !target) return false;
  return (
    areSiblingDepartments(depts, sourceIndex, targetIndex) ||
    canPromoteDepartmentToRoot(source, target)
  );
};

// Returns null when the drop is valid, else a user-facing reason string.
const getInvalidDropReason = (draggedDept, targetDept, position, depts) => {
  if (position === "inside") {
    if (!canNestDepartmentInside(draggedDept, targetDept)) {
      return "An active department's parent must be active.";
    }
    return null;
  }

  const sourceIndex = depts.findIndex((d) => d.id === draggedDept.id);
  const targetIndex = depts.findIndex((d) => d.id === targetDept.id);
  if (!canDropDepartmentBeforeOrAfter(depts, sourceIndex, targetIndex)) {
    return "Departments can only be reordered among sibling departments, or moved to the top level.";
  }
  return null;
};

const updateSubtreeLevels = (subtree, levelDelta) => {
  subtree.forEach((d) => {
    d.level = Math.max(0, (d.level || 0) + levelDelta);
  });
};

const handleDragStart = (dept, event) => {
  draggedDept.value = dept;
  event.dataTransfer.effectAllowed = "move";
  event.dataTransfer.setData("text/plain", dept.id);
};

const handleDragOver = (dept, event) => {
  if (!draggedDept.value || draggedDept.value.id === dept.id) return;
  startAutoScroll(event.clientY);

  const draggedSubtree = getSubtree(draggedDept.value.id, internalDepartments.value);
  if (draggedSubtree.some((d) => d.id === dept.id)) return;

  const rect = event.currentTarget.getBoundingClientRect();
  const y = event.clientY - rect.top;
  const height = rect.height;

  const depts = internalDepartments.value;
  const canPromoteToRoot = canPromoteDepartmentToRoot(draggedDept.value, dept);

  let position = null;
  if (y >= height * 0.25 && y <= height * 0.75) {
    position = "inside";
  } else if (y < height * 0.25) {
    position = "before";
  } else if (y > height * 0.75) {
    position = "after";
  }

  const reason = getInvalidDropReason(draggedDept.value, dept, position, depts);

  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = reason ? "none" : "move";
  }

  if (reason) {
    invalidDropTargetId.value = dept.id;
    invalidDropReason.value = reason;
    invalidDropCoords.value = {
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
    };
    dropTargetId.value = null;
    dropPosition.value = null;
    dropPromoteToRoot.value = false;
    tableRootDropActive.value = false;
    return;
  }

  invalidDropTargetId.value = null;
  invalidDropReason.value = null;

  if (position === "inside") {
    dropTargetId.value = dept.id;
    dropPosition.value = "inside";
    dropPromoteToRoot.value = false;
    tableRootDropActive.value = false;
  } else if (position === "before") {
    dropTargetId.value = dept.id;
    dropPosition.value = "before";
    dropPromoteToRoot.value = canPromoteToRoot;
    tableRootDropActive.value = false;
  } else if (position === "after") {
    dropTargetId.value = dept.id;
    dropPosition.value = "after";
    dropPromoteToRoot.value = canPromoteToRoot;
    tableRootDropActive.value = false;
  }
};

const handleTableContainerDragOver = (event) => {
  if (!draggedDept.value || (draggedDept.value.level ?? 0) === 0) return;
  const tbody = event.currentTarget?.querySelector?.("tbody");
  const rows = tbody?.querySelectorAll?.("tr");
  if (!rows?.length) return;
  const lastRect = rows[rows.length - 1].getBoundingClientRect();
  if (event.clientY <= lastRect.bottom) return;

  startAutoScroll(event.clientY);
  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";

  const depts = internalDepartments.value;
  let lastRootId = null;
  for (let i = depts.length - 1; i >= 0; i -= 1) {
    if ((depts[i].level ?? 0) === 0) {
      lastRootId = depts[i].id;
      break;
    }
  }
  if (lastRootId != null) {
    dropTargetId.value = lastRootId;
    dropPosition.value = "after";
    dropPromoteToRoot.value = true;
    tableRootDropActive.value = true;
    invalidDropTargetId.value = null;
    invalidDropReason.value = null;
  }
};

const handleTableContainerDrop = async (event) => {
  if (!draggedDept.value || (draggedDept.value.level ?? 0) === 0) return;
  const depts = internalDepartments.value;
  let lastRoot = null;
  for (let i = depts.length - 1; i >= 0; i -= 1) {
    if ((depts[i].level ?? 0) === 0) {
      lastRoot = depts[i];
      break;
    }
  }
  if (!lastRoot) return;
  await handleDrop(lastRoot, event);
};

const handleDragLeave = (dept, event) => {
  if (dropTargetId.value === dept.id) {
    dropTargetId.value = null;
    dropPosition.value = null;
    dropPromoteToRoot.value = false;
    tableRootDropActive.value = false;
  }
  if (invalidDropTargetId.value === dept.id) {
    invalidDropTargetId.value = null;
    invalidDropReason.value = null;
  }
};

const resolveDropPosition = (targetDept, event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const y = event.clientY - rect.top;
  const height = rect.height;

  if (y >= height * 0.25 && y <= height * 0.75) return "inside";

  const depts = internalDepartments.value;
  const sourceIndex = depts.findIndex((d) => d.id === draggedDept.value?.id);
  const targetIndex = depts.findIndex((d) => d.id === targetDept.id);
  if (!canDropDepartmentBeforeOrAfter(depts, sourceIndex, targetIndex)) return null;

  return y < height * 0.5 ? "before" : "after";
};

const handleDrop = async (targetDept, event) => {
  if (!draggedDept.value || draggedDept.value.id === targetDept.id) return;

  const depts = [...internalDepartments.value];
  const sourceId = draggedDept.value.id;
  const targetId = targetDept.id;

  const sourceIndex = depts.findIndex((d) => d.id === sourceId);
  if (sourceIndex === -1) return;

  const subtree = getSubtree(sourceId, depts);

  // Dragging a subtree into itself is already blocked in dragover,
  // but let's be double sure.
  if (subtree.some((d) => d.id === targetId)) return;

  // Recalculate position from event geometry when state is stale (e.g. dragleave fired before drop)
  const resolvedDropPosition =
    dropTargetId.value === targetId ? dropPosition.value : resolveDropPosition(targetDept, event);

  if (!resolvedDropPosition) {
    handleDragEnd();
    return;
  }

  // An active department cannot be nested inside an inactive department
  // (the backend enforces "An active department's parent must be active").
  if (
    resolvedDropPosition === "inside" &&
    !canNestDepartmentInside(draggedDept.value, targetDept)
  ) {
    handleDragEnd();
    return;
  }

  // before/after drops are valid between siblings or when promoting a nested department to root
  if (resolvedDropPosition !== "inside") {
    const targetIndex = depts.findIndex((d) => d.id === targetId);
    if (!canDropDepartmentBeforeOrAfter(depts, sourceIndex, targetIndex)) {
      handleDragEnd();
      return;
    }
  }

  const promotingToRoot =
    resolvedDropPosition !== "inside" && canPromoteDepartmentToRoot(draggedDept.value, targetDept);

  // Remove subtree from its original position
  depts.splice(sourceIndex, subtree.length);

  // Find new target index
  let newTargetIndex = depts.findIndex((d) => d.id === targetId);
  if (newTargetIndex === -1) return;

  if (resolvedDropPosition === "inside") {
    // Make it a child of targetDept
    const targetLevel = targetDept.level || 0;
    updateSubtreeLevels(subtree, targetLevel + 1 - (draggedDept.value.level || 0));
    // Insert after targetDept
    depts.splice(newTargetIndex + 1, 0, ...subtree);
    // Expand target if it was collapsed or update its state
    const target = depts[newTargetIndex];
    if (target.expansionState === "Collapsed" || target.expansionState === "No Expand") {
      target.expansionState = "Expanded";
    }

    try {
      const accountID = localStorage.getItem("currentAccountID");
      await editDepartment(accountID, sourceId, { parent_department: targetId });
    } catch (error) {
      console.error("Error reparenting department:", error);
      const apiError =
        normalizeApiErrorText(error?.response?.data?.detail) ||
        normalizeApiErrorText(error?.response?.data?.error) ||
        normalizeApiErrorText(error?.response?.data?.[0]) ||
        normalizeApiErrorText(error?.response?.data?.parent_department?.[0]);
      const message =
        apiError || "Cannot move this department. Its parent department must be active.";
      proxy?.$notifications?.error(message);
    }

    handleDragEnd();
    await reloadDepartments();
    return;
  } else if (resolvedDropPosition === "before") {
    // Sibling before targetDept
    updateSubtreeLevels(subtree, (targetDept.level || 0) - (draggedDept.value.level || 0));
    depts.splice(newTargetIndex, 0, ...subtree);
  } else if (resolvedDropPosition === "after") {
    // Sibling after targetDept
    const targetSubtree = getSubtree(targetId, depts);
    updateSubtreeLevels(subtree, (targetDept.level || 0) - (draggedDept.value.level || 0));
    depts.splice(newTargetIndex + targetSubtree.length, 0, ...subtree);
  }

  // Update internalDepartments and expand/collapse states
  internalDepartments.value = depts;

  // Update all items' expansion states if they now have or don't have children
  updateAllExpansionStates();

  if (promotingToRoot) {
    try {
      const accountID = localStorage.getItem("currentAccountID");
      await editDepartment(accountID, sourceId, { parent_department: null });
    } catch (error) {
      console.error("Error promoting department to root:", error);
      const apiError =
        normalizeApiErrorText(error?.response?.data?.detail) ||
        normalizeApiErrorText(error?.response?.data?.error) ||
        normalizeApiErrorText(error?.response?.data?.[0]) ||
        normalizeApiErrorText(error?.response?.data?.parent_department?.[0]);
      const message = apiError || "Cannot move this department. Please try again.";
      proxy?.$notifications?.error(message);
    }
    handleDragEnd();
    await reloadDepartments();
    return;
  }

  // Sibling reorder - persist to backend
  try {
    const accountID = localStorage.getItem("currentAccountID");
    const newSequence = getSiblingSequence(
      depts,
      depts.findIndex((d) => d.id === sourceId)
    );
    await reorderDepartment(accountID, sourceId, { new_sequence: newSequence });
  } catch (error) {
    console.error("Error reordering department:", error);
  }

  handleDragEnd();
  emit("reorder", internalDepartments.value);
  await reloadDepartments();
};

const updateAllExpansionStates = () => {
  const depts = internalDepartments.value;
  for (let i = 0; i < depts.length; i++) {
    const current = depts[i];
    const nextItem = depts[i + 1];
    const hasChildren = nextItem && (nextItem.level || 0) > (current.level || 0);

    if (!hasChildren) {
      current.expansionState = "No Expand";
    } else if (current.expansionState === "No Expand") {
      current.expansionState = "Expanded";
    }
    // If it already had children and was Expanded/Collapsed, keep it as is.
  }
};

const handleDragEnd = () => {
  stopAutoScroll();
  draggedDept.value = null;
  dropTargetId.value = null;
  dropPosition.value = null;
  dropPromoteToRoot.value = false;
  tableRootDropActive.value = false;
  draggableId.value = null;
  invalidDropTargetId.value = null;
  invalidDropReason.value = null;
};

let unbindAccessFiltersChanged = () => {};

onMounted(async () => {
  await dropdownItems.initializeFlags();
  applySavedFilters();
  unbindAccessFiltersChanged = bindExiqtiveAccessFiltersChanged(async () => {
    applySavedFilters();
    await loadDepartments({
      filters: convertDepartmentFiltersToApi(appliedFilters.value),
    });
    await refreshPaneTreeIfActive();
  });
  departmentPositionFilterChips.value = defaultDepartmentPositionFilters.map((key) => ({
    key,
    label: key.charAt(0).toUpperCase() + key.slice(1),
  }));
  debouncedSearch = debounce(async (searchText) => {
    await loadDepartments({
      search: searchText,
      filters: convertDepartmentFiltersToApi(appliedFilters.value),
    });
    await refreshPaneTreeIfActive();
  }, 500);
  loadDepartments();
  loadOrphanPositions();
});

onUnmounted(() => {
  unbindAccessFiltersChanged();
  stopAutoScroll();
  handlePaneResizeEnd();
  detachPaneViewRightHeightObserver();
  removeLeftPanelWheelListener();
});

watch(departmentTreeData, async (tree) => {
  if (viewSelection.value === "tree-with-positions") {
    if (tree.length === 0) return;
    if (!selectedDepartment.value && tree.length > 0) {
      selectFirstDepartment();
      return;
    }
    if (selectedDepartment.value && !findDepartmentInTree(tree, selectedDepartment.value.id)) {
      selectFirstDepartment();
    }
    await ensurePaneViewHeightSync();
    return;
  }
  if (viewSelection.value === "orphan-positions" && tree.length > 0) {
    await expandFirstDepartmentInTree();
    await ensurePaneViewHeightSync();
  }
});

watch(
  () => viewSelection.value,
  async (mode) => {
    if (mode === "tree-with-positions" || mode === "orphan-positions") {
      await ensurePaneViewHeightSync();
      return;
    }
    removeLeftPanelWheelListener();
    detachPaneViewRightHeightObserver();
    paneViewRightHeight.value = null;
  }
);
</script>

<style>
.pane-checkpoint-drag-cursor-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  cursor:
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E") 0 0,
    none !important;
  pointer-events: none;
  background: transparent;
}

.pane-checkpoint-drag-cursor {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 2147483647;
  pointer-events: none;
  display: block;
  transform: translateZ(0);
}

html.checkpoint-drag-cursor-active,
html.checkpoint-drag-cursor-active *,
body.checkpoint-drag-cursor-active,
body.checkpoint-drag-cursor-active * {
  cursor:
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E") 0 0,
    none !important;
}
</style>

<style scoped>
.organization-departments-table {
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow:
    0px 1px 3px rgba(16, 24, 40, 0.1),
    0px 1px 2px rgba(16, 24, 40, 0.06);
}

.departments-header-dashboard-wrapper {
  padding: 16px 16px 0;
}

.simple-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.simple-table th {
  background-color: #f9fafb;
  color: #667085;
  font-size: 12px;
  font-weight: 500;
  padding: 10px 24px;
  border-bottom: 1px solid #eaecf0;
  text-transform: capitalize;
}

.departments-header {
  width: auto;
}

.positions-count-header {
  width: 140px;
  white-space: nowrap;
  padding-right: 24px;
}

.positions-count-col {
  width: 140px;
  white-space: nowrap;
  color: #344054;
  font-size: 14px;
}

.positions-subtree-count-header {
  width: 180px;
  white-space: nowrap;
  padding-right: 24px;
}

.positions-subtree-count-col {
  width: 180px;
  white-space: nowrap;
  color: #344054;
  font-size: 14px;
}

.tree-row--navigable {
  cursor: pointer;
}

.status-header,
.status-actions-col {
  width: 220px;
  white-space: nowrap;
}

.status-header-inner {
  width: 100%;
}

.status-header-actions-spacer {
  min-width: 100px;
  margin-left: 24px;
  flex-shrink: 0;
}

.simple-table td {
  padding: 0px 24px;
  border-bottom: 1px solid #f2f4f7;
  vertical-align: middle;
}

.dept-col {
  width: auto;
  overflow: hidden;
}

.status-actions-col {
  padding-right: 24px !important;
}

::v-deep .dept-col .department-tree-card {
  width: 100%;
  min-width: 0;
}

::v-deep .dept-col .org-object-card-container {
  flex: 1;
  min-width: 0;
  width: 100%;
}

::v-deep .dept-col .org-object-card-compass {
  max-width: 100%;
  width: auto;
}

::v-deep .dept-col .org-object-card-compass .code-text,
::v-deep .dept-col .org-object-card-compass .name-text {
  max-width: none;
}

.icon-action-container {
  min-width: 100px;
  justify-content: flex-end;
}

.simple-table tr:last-child td {
  border-bottom: none;
}

.simple-table tr:hover {
  background-color: #f9fafb;
}

/* Drag and Drop Styles */
.simple-table tr.dragging {
  opacity: 0.5;
  background-color: #f2f4f7;
}

.simple-table tr.drop-before {
  border-top: 2px solid #7f56d9;
}

.simple-table tr.drop-after {
  border-bottom: 2px solid #7f56d9;
}

.simple-table tr.drop-inside {
  background-color: #f4f0fd;
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.simple-table tr.drop-not-allowed {
  cursor: not-allowed;
  background-color: #fee4e2;
  outline: 2px solid #f04438;
  outline-offset: -2px;
}

.simple-table tr.drop-promote-root.drop-before {
  border-top: 3px solid #7f56d9;
  box-shadow: inset 0 6px 0 0 #f4f0fd;
}

.simple-table tr.drop-promote-root.drop-after {
  border-bottom: 3px solid #7f56d9;
  box-shadow: inset 0 -6px 0 0 #f4f0fd;
}

.simple-table tr.drop-promote-root.drop-before td:first-child,
.simple-table tr.drop-promote-root.drop-after td:first-child {
  background: linear-gradient(90deg, #f4f0fd 0%, #f4f0fd 40px, transparent 40px);
}

.table-container--root-drop-zone {
  box-shadow: inset 0 -3px 0 0 #7f56d9;
  background: linear-gradient(180deg, transparent calc(100% - 28px), #f4f0fd calc(100% - 28px));
}

.department-drag-invalid-label {
  position: fixed;
  z-index: 3000;
  pointer-events: none;
  background-color: #0c111d;
  color: #ffffff;
  font-size: 12px;
  font-family: Inter, system-ui, -apple-system, sans-serif;
  line-height: 18px;
  border-radius: 6px;
  padding: 8px 12px;
  white-space: nowrap;
}

.icon-action {
  display: inline-flex;
  cursor: pointer;
  color: #98a2b3;
  padding: 8px;
  border-radius: 8px;
  transition:
    background-color 0.2s,
    color 0.2s;
}

.chatter-clickable {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.chatter-clickable:hover {
  background-color: #f2f4f7;
}

.department-menu-action {
  padding: 0;
}

.department-menu-action :deep(.department-line-menu) {
  width: auto;
}

.text-left {
  text-align: left;
}
.text-right {
  text-align: right;
}
.text-center {
  text-align: center;
}

/* Custom styles to match the image precisely */
::v-deep .search-bar-container {
  max-width: 600px;
}

::v-deep .positions-view-selector {
  border: 1px solid #d0d5dd;
  background-color: #fff;
  height: 40px;
}

/* Adjust search bar layout to match the image spacing */
::v-deep .search-bar .filter-button {
  gap: 12px;
}

/* idea-MDP-450: enough width for "Search Departments by name or code" / position placeholder */
.organization-departments-table--idea-mdp-450 ::v-deep .filter-input-search {
  max-width: 350px !important;
  width: 350px !important;
}

.organization-departments-table--idea-mdp-450 ::v-deep .v-text-field .v-label {
  max-width: 350px !important;
}

/* idea-MDP-450: DepartmentCard unassigned search fills the filters header */
.organization-departments-table--idea-mdp-450
  ::v-deep
  .department-card__filters--wide-search
  .filter-input-search {
  width: 100% !important;
  max-width: 100%;
  float: none;
}

::v-deep .search-add-btn {
  background-color: #7f56d9 !important;
  color: #fff !important;
}

::v-deep .search-add-btn .storybook-button {
  background-color: #7f56d9 !important;
  color: #fff !important;
}

.departments-pane-view,
.departments-orphan-view {
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
}

.departments-pane-header {
  gap: 16px;
}

.departments-pane-header__view-selector {
  flex-shrink: 0;
}

.departments-pane-banner {
  display: flex;
  padding: 20px 48px;
  align-items: flex-start;
  flex: 1 0 0;
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  border-radius: 12px;
  border: 1px solid #7f56d9;
  background: #6941c6;
  box-shadow:
    0 12px 16px -4px rgba(16, 24, 40, 0.08),
    0 4px 6px -2px rgba(16, 24, 40, 0.03);
}

.lighter-text {
  overflow: hidden;
  color: #e9d7fe !important;
  text-overflow: ellipsis;
  font-size: 16px !important;
  font-weight: 400 !important;
  line-height: 24px !important;
}

.departments-pane-banner--orphan {
  background: #7f56d9;
  border-color: #6941c6;
  color: #fff;
  justify-content: center;
  text-align: center;
}

.pane-view-layout {
  display: flex;
  gap: 0;
  flex: 1;
  min-height: 0;
  width: 100%;
  align-items: stretch;
}

.pane-view-left {
  flex: 0 0 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid #d1caca;
  border-radius: 16px;
  padding: 10px;
  scroll-behavior: smooth;
  height: 100%;
  position: sticky;
  top: 0;
}

.pane-view-left-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

.pane-view-left-scroll.pane-view-left-scroll--has-content {
  overflow-y: scroll;
}

.pane-view-left-scroll.pane-view-left-scroll--empty {
  overflow-y: hidden;
}

.pane-view-left-scroll::-webkit-scrollbar {
  width: 10px;
  height: 0;
}

.pane-view-left-scroll::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.pane-view-left-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.pane-view-left-scroll::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}

.pane-view-left-scroll::-webkit-scrollbar-corner {
  background: transparent;
  display: none;
}

.pane-view-resize-handle {
  flex: 0 0 8px;
  width: 8px;
  min-height: 0;
  cursor: col-resize;
  background: transparent;
  align-self: stretch;
  position: relative;
}

.pane-view-resize-handle:hover,
.pane-view-resize-handle:active {
  background: rgba(0, 0, 0, 0.06);
}

.pane-view-resize-handle::after {
  content: "";
  position: absolute;
  left: 3px;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 40px;
  border-radius: 1px;
  background: #d1caca;
  opacity: 0.6;
}

.pane-view-right {
  flex: 1;
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  min-width: 0;
}

.pane-view-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  background: #fff;
}

.pane-view-empty .empty-state p {
  margin: 0;
  color: #667085;
  font-size: 14px;
}
</style>
